import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2, Plus, MapPinned, Truck } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx-js-style';
import { warehouseService } from '../services/warehouseService';
import { customerService } from '../services/customerService';
import { supplyService } from '../services/supplyService';
import { apiService } from '../services/apiService';
import { useAppAlert } from './AppAlertProvider';

type ExcelRow = {
  arac_no: string;
  alici_kodu: string;
  alici_adi: string;
  sehir: string;
  ilce: string;
  arac_tipi: string;
  not: string;
  yukturu: string;
  tonaj: number;
};

type DetailExcelRow = {
  arac_no: string;
  siparis_kodu: string;
  alici_kodu: string;
  alici_adi: string;
  sehir: string;
  ilce: string;
  m3toplam: number;
  yukleme_tipi: string;
  palet: number;
  not: string;
};

type ParsedShipment = {
  arac_no: string;
  planCode: string;
  sourceShipmentId?: string;
  assignmentStartDate?: string;
  assignmentStartTime?: string;
  pickupDate?: string | null;
  pickupWarehouseId: string;
  pickupWarehouseName: string;
  pickupCity?: string;
  pickupDistrict?: string;
  pickupAddress?: string;
  pickupLatitude?: number | null;
  pickupLongitude?: number | null;
  pickupServiceDurationMinutes?: number | null;
  deliveryCustomerCode: string;
  deliveryCustomerName: string;
  deliveryCustomerCity: string;
  deliveryCustomerDistrict: string;
  deliveryChannelType?: string;
  deliveryLatitude?: number | null;
  deliveryLongitude?: number | null;
  deliveryAcceptanceStartTime?: string | null;
  deliveryAcceptanceEndTime?: string | null;
  deliveryServiceDurationMinutes?: number | null;
  deliveryWaitDurationMinutes?: number | null;
  stopCount: number;
  stops: { customerCode: string; customerName: string; city: string; district: string; volume: number; loadType: string; tonnage: number; existingCustomerId?: string; customerType?: 'customer' | 'warehouse'; latitude?: number | null; longitude?: number | null; address?: string; channelType?: string; acceptanceStartTime?: string | null; acceptanceEndTime?: string | null; serviceDurationMinutes?: number | null; waitDurationMinutes?: number | null }[];
  totalVolume: number;
  totalTonnage: number;
  loadTypes: string[];
  vehicleType: string;
  notes: string[];
  existingCustomerId?: string;
  excludeFromKds?: boolean;
};

type DecisionSupportPreview = {
  shipmentCount: number;
  vehicleCount: number;
  replicationCount: number;
  durationMs?: number;
  searchMethod?: string;
  distanceProvider?: string;
  bestWorstCostGap: number;
  worstScenario?: {
    scenarioNo: number;
    summary: {
      totalSelectedCost: number;
    };
  };
  selectedScenario: {
    scenarioNo: number;
    decisions: Array<{
      shipmentId: string | number;
      shipmentNo: string;
      planCode: string;
      pickupName: string;
      pickupCity?: string;
      pickupDistrict?: string;
      pickupLatitude?: number | null;
      pickupLongitude?: number | null;
      deliveryName: string;
      deliveryCity?: string;
      deliveryDistrict?: string;
      deliveryChannelType?: string | null;
      stopCount?: number;
      deliveryAcceptanceWindowLabel?: string | null;
      decision: 'OZMAL' | 'SPOT';
      assignedVehicleId?: number | null;
      assignedVehiclePlate: string;
      vehicleLatitude?: number | null;
      vehicleLongitude?: number | null;
      selectedTotalCost: number;
      spotAlternativeCost: number;
      bestOwnAlternativeCost: number | null;
      emptyKm: number;
      routeKm: number;
      totalKm: number;
      operationHours: number;
      operationDayCount: number;
      waitingCost: number;
      nightCost: number;
      bridgeCost: number;
      shortDistancePrime?: number;
      kmCost?: number;
      personnelCost?: number;
      dailyPersonnelCost?: number;
      personnelCostMode?: 'daily' | 'hourly';
      hourlyPersonnelCost?: number;
      chargedPersonnelHours?: number;
      personnelDayCount?: number;
      distanceMethod?: string;
      returnHubKm?: number;
      returnHubName?: string | null;
      returnHubLatitude?: number | null;
      returnHubLongitude?: number | null;
      driverName?: string | null;
      residenceCity?: string | null;
      remainingTakoHours?: number | null;
      remainingWeeklyDriveHours?: number | null;
      remainingTwoWeekDriveHours?: number | null;
      weeklyDriveCompatible?: boolean | null;
      twoWeekDriveCompatible?: boolean | null;
      locationSource?: string | null;
      driverToPickupKm?: number;
      driverToPickupDistanceMethod?: string | null;
      estimatedPickupArrivalAt?: string;
      estimatedRouteFinishAt?: string;
      estimatedNearestHubArrivalAt?: string;
      totalDriveHours?: number;
      totalBreakHours?: number;
      totalRestHours?: number;
      totalServiceHours?: number;
      totalWaitingHours?: number;
      stopSchedules?: Array<{ sequenceNo: number; label: string; latitude?: number | null; longitude?: number | null; arrivalAt?: string; serviceStartAt?: string; serviceEndAt?: string; acceptanceWindowLabel?: string | null; windowMatched?: boolean }>;
      nearestOwnAlternative?: {
        assignedVehicleId?: number | null;
        assignedVehiclePlate: string;
        vehicleLatitude?: number | null;
        vehicleLongitude?: number | null;
        selectedTotalCost: number;
        emptyKm: number;
        routeKm: number;
        totalKm: number;
        returnHubKm: number;
        returnHubLatitude?: number | null;
        returnHubLongitude?: number | null;
        operationHours: number;
        operationDayCount: number;
        estimatedPickupArrivalAt?: string;
        estimatedRouteFinishAt?: string;
        estimatedNearestHubArrivalAt?: string;
        waitingCost: number;
        nightCost: number;
        perNightCost?: number;
        bridgeCost: number;
        shortDistancePrime?: number;
        kmCost?: number;
        personnelCost?: number;
        dailyPersonnelCost?: number;
        personnelCostMode?: 'daily' | 'hourly';
        hourlyPersonnelCost?: number;
        chargedPersonnelHours?: number;
        personnelDayCount?: number;
        distanceMethod?: string;
        returnHubName?: string | null;
        driverName?: string | null;
        residenceCity?: string | null;
        remainingTakoHours?: number | null;
        remainingWeeklyDriveHours?: number | null;
        remainingTwoWeekDriveHours?: number | null;
        weeklyDriveCompatible?: boolean | null;
        twoWeekDriveCompatible?: boolean | null;
        locationSource?: string | null;
        driverToPickupKm?: number;
        driverToPickupDistanceMethod?: string | null;
        pickupServiceDurationMinutes?: number;
        deliveryAcceptanceWindowLabel?: string | null;
        totalDriveHours?: number;
        totalBreakHours?: number;
        totalRestHours?: number;
        totalServiceHours?: number;
        totalWaitingHours?: number;
        stopSchedules?: Array<{ sequenceNo: number; label: string; latitude?: number | null; longitude?: number | null; arrivalAt?: string; serviceStartAt?: string; serviceEndAt?: string; acceptanceWindowLabel?: string | null; windowMatched?: boolean }>;
        totalWaitingMinutes?: number;
      } | null;
      nearestOwnExclusionReason?: string | null;
      selectedOwnReason?: string | null;
      ownAlternativeExplanations?: Array<{
        assignedVehicleId?: number | null;
        assignedVehiclePlate: string;
        driverName?: string | null;
        selectedTotalCost: number;
        driverToPickupKm?: number;
        routeKm?: number;
        totalKm?: number;
        selected: boolean;
        reason: string;
        matchesPreferredDeliveryPoint?: boolean;
        matchesRegionRestriction?: boolean;
        weeklyDriveCompatible?: boolean | null;
        twoWeekDriveCompatible?: boolean | null;
      }>;
      weeklyRemainingBefore?: number | null;
      weeklyRemainingAfter?: number | null;
      twoWeekRemainingBefore?: number | null;
      twoWeekRemainingAfter?: number | null;
      assignmentStartTime?: string | null;
      spotAlternative?: {
        selectedTotalCost: number;
        directSpotCost?: number;
        spotKmCost?: number;
        returnHubKm?: number;
        returnHubName?: string | null;
        returnHubLatitude?: number | null;
        returnHubLongitude?: number | null;
        operationHours: number;
        operationDayCount: number;
        estimatedPickupArrivalAt?: string;
        estimatedRouteFinishAt?: string;
        estimatedNearestHubArrivalAt?: string;
        waitingCost: number;
        nightCost: number;
        bridgeCost: number;
        pickupServiceDurationMinutes?: number;
        deliveryAcceptanceWindowLabel?: string | null;
        stopSchedules?: Array<{ sequenceNo: number; label: string; latitude?: number | null; longitude?: number | null; arrivalAt?: string; serviceStartAt?: string; serviceEndAt?: string; acceptanceWindowLabel?: string | null; windowMatched?: boolean }>;
        totalWaitingMinutes?: number;
      };
    }>;
    summary: {
      ozmalAssigned: number;
      spotAssigned: number;
      totalSelectedCost: number;
      totalSpotReferenceCost: number;
    };
  };
  scenarios: Array<{
    scenarioNo: number;
    scenarioType: string;
    shipmentOrder?: string[];
    summary: {
      ozmalAssigned: number;
      spotAssigned: number;
      totalSelectedCost: number;
      totalSpotReferenceCost: number;
    };
  }>;
  bmsLogs?: Array<{
    attempt: number;
    status: number;
    ok: boolean;
    message: string;
    km?: number;
  }>;
};

type DecisionPreviewJobSnapshot = {
  jobId: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  startedAt?: string | null;
  completedAt?: string | null;
  durationMs?: number;
  error?: string | null;
  progress?: {
    percent?: number;
    phase?: string;
    message?: string;
    stageCurrent?: number;
    stageTotal?: number;
    stageSecondaryCurrent?: number;
    stageSecondaryTotal?: number;
    shipmentCount?: number;
    replicationCount?: number;
    searchMethod?: string;
    distanceProvider?: string;
    matrixNodeCount?: number;
    matrixElementCount?: number;
    routeCompleted?: number;
    routeTotal?: number;
    shipmentCompleted?: number;
    shipmentTotal?: number;
    scenarioCompleted?: number;
    scenarioTotal?: number;
  };
  data?: DecisionSupportPreview;
};

type DecisionSupportEstimate = {
  shipmentCount: number;
  activeOwnVehicleCount: number;
  activeHubCount: number;
  uniquePickupCount: number;
  vehiclePickupPairs: number;
  routeMatrixElements: number;
  deliveryHubPairs: number;
  totalEstimatedBmsPairs: number;
  candidateVehicles?: Array<{
    vehicleId: number | string;
    assignedVehiclePlate: string;
    driverName?: string | null;
    currentAddress?: string | null;
    distanceToPickupKm?: number | null;
    nearestPickupPlanCode?: string | null;
    nearestPickupName?: string | null;
    nearestPickupCity?: string | null;
    nearestPickupDistrict?: string | null;
    isWithinDistanceLimit?: boolean;
    isVehicleActive?: boolean;
    isDriverActive?: boolean;
    hasActiveTask?: boolean;
    activeTaskCount?: number;
    activeTaskCodes?: string[];
    exclusionReason?: string;
    isAssignable?: boolean;
  }>;
};

type ValidationStatus = 'pending' | 'ok' | 'warehouse' | 'missing';

const normalizeCustomerMatchValue = (value: unknown) => String(value ?? '')
  .trim()
  .toLocaleLowerCase('tr-TR')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]/gi, '');

const findExistingCustomer = (customers: any[], code?: string, name?: string) => {
  const normalizedCode = normalizeCustomerMatchValue(code);
  const normalizedName = normalizeCustomerMatchValue(name);
  return customers.find((customer) => {
    const codeCandidates = [
      customer?.company_code,
      customer?.customer_code,
      customer?.code,
      customer?.companyCode,
      customer?.customerCode,
    ].map(normalizeCustomerMatchValue).filter(Boolean);
    const nameCandidates = [
      customer?.company_name,
      customer?.name,
      customer?.companyName,
    ].map(normalizeCustomerMatchValue).filter(Boolean);
    if (normalizedCode && codeCandidates.includes(normalizedCode)) return true;
    if (normalizedName && nameCandidates.includes(normalizedName)) return true;
    return false;
  });
};

const findExistingWarehouseByCode = (items: any[], code?: string) => {
  const normalizedCode = normalizeCustomerMatchValue(code);
  if (!normalizedCode) return undefined;

  return items.find((warehouse) => [
    warehouse?.code,
    warehouse?.warehouse_code,
    warehouse?.warehouseCode,
    warehouse?.shipment_code,
    warehouse?.shipmentCode,
    warehouse?.stokbar_code,
    warehouse?.stokbarCode,
  ].map(normalizeCustomerMatchValue).filter(Boolean).includes(normalizedCode));
};

const mapWarehouseToCustomerLike = (warehouse: any, fallbackCode?: string, fallbackName?: string) => ({
  id: null,
  code: warehouse.code || warehouse.warehouse_code || warehouse.warehouseCode || warehouse.shipment_code || warehouse.shipmentCode || warehouse.stokbar_code || warehouse.stokbarCode || fallbackCode || null,
  company_code: warehouse.code || warehouse.warehouse_code || warehouse.warehouseCode || warehouse.shipment_code || warehouse.shipmentCode || warehouse.stokbar_code || warehouse.stokbarCode || fallbackCode || null,
  customer_code: warehouse.code || warehouse.warehouse_code || warehouse.warehouseCode || warehouse.shipment_code || warehouse.shipmentCode || warehouse.stokbar_code || warehouse.stokbarCode || fallbackCode || null,
  name: warehouse.name || fallbackName || fallbackCode || 'Depo',
  company_name: warehouse.name || fallbackName || fallbackCode || 'Depo',
  city: warehouse.city || null,
  district: warehouse.district || null,
  address: warehouse.address || null,
  latitude: warehouse.latitude ?? null,
  longitude: warehouse.longitude ?? null,
  customer_type: 'warehouse',
});

function CostTooltip({ children, breakdown }: { children: React.ReactNode; breakdown: React.ReactNode }) {
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  const openTooltip = (element: HTMLDivElement) => {
    const rect = element.getBoundingClientRect();
    setPosition({
      left: rect.left + (rect.width / 2),
      top: rect.top - 12,
    });
    setShow(true);
  };

  return (
    <div
      className="relative inline-block"
      onMouseEnter={(e) => openTooltip(e.currentTarget)}
      onFocus={(e) => openTooltip(e.currentTarget)}
      onMouseLeave={() => setShow(false)}
      onBlur={() => setShow(false)}
    >
      {children}
      {show && position && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed z-[99999] min-w-[280px] max-w-[420px] -translate-x-1/2 -translate-y-full rounded-xl border border-slate-200 bg-white p-3 shadow-2xl text-xs text-slate-700 whitespace-normal"
          style={{ left: position.left, top: position.top }}
        >
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-8 border-transparent border-t-white" />
          {breakdown}
        </div>,
        document.body,
      )}
    </div>
  );
}

const getOwnCostBreakdownTotal = (alt?: {
  kmCost?: number;
  personnelCost?: number;
  waitingCost?: number;
  shortDistancePrime?: number;
  nightCost?: number;
  bridgeCost?: number;
} | null) => Math.round(
  Number(alt?.kmCost || 0)
  + Number(alt?.personnelCost || 0)
  + Number(alt?.waitingCost || 0)
  + Number(alt?.shortDistancePrime || 0)
  + Number(alt?.nightCost || 0)
  + Number(alt?.bridgeCost || 0)
);

const getDisplayedOwnAlternative = (item: DecisionSupportPreview['selectedScenario']['decisions'][number]) => (
  item.decision === 'OZMAL' ? item : item.nearestOwnAlternative
);

const getDisplayedWeeklyRemainingHours = (item: DecisionSupportPreview['selectedScenario']['decisions'][number]) => {
  const own = getDisplayedOwnAlternative(item);
  return own?.remainingWeeklyDriveHours ?? null;
};

const getDisplayedTwoWeekRemainingHours = (item: DecisionSupportPreview['selectedScenario']['decisions'][number]) => {
  const own = getDisplayedOwnAlternative(item);
  return own?.remainingTwoWeekDriveHours ?? null;
};

const getDisplayedDeliveryStops = (item: DecisionSupportPreview['selectedScenario']['decisions'][number]) => {
  const stops = item.stopSchedules?.length
    ? item.stopSchedules
    : (item.nearestOwnAlternative?.stopSchedules?.length
      ? item.nearestOwnAlternative.stopSchedules
      : item.spotAlternative?.stopSchedules || []);
  return stops.map((stop) => stop.label).filter(Boolean);
};

const getDisplayedStopSchedules = (item: DecisionSupportPreview['selectedScenario']['decisions'][number]) => (
  item.stopSchedules?.length
    ? item.stopSchedules
    : (item.nearestOwnAlternative?.stopSchedules?.length
      ? item.nearestOwnAlternative.stopSchedules
      : item.spotAlternative?.stopSchedules || [])
);

const hashString = (value: string) => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) - hash) + value.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash);
};

const toHexColor = (red: number, green: number, blue: number) => `#${[red, green, blue].map((value) => Math.max(0, Math.min(255, value)).toString(16).padStart(2, '0')).join('')}`.toUpperCase();

const getVehicleRowColor = (plate: string | null | undefined) => {
  const normalized = String(plate || '').trim().toUpperCase();
  if (!normalized) return null;
  const hash = hashString(normalized);
  return {
    bg: toHexColor(214 + (hash % 24), 214 + ((hash >> 5) % 24), 214 + ((hash >> 10) % 24)),
    border: toHexColor(70 + (hash % 120), 70 + ((hash >> 7) % 120), 70 + ((hash >> 14) % 120)),
  };
};

const getVehicleCellStyle = (tone: { bg: string; border: string } | null, isSelected: boolean, isFirstColumn = false) => {
  if (!tone) return undefined;
  return {
    backgroundColor: isSelected ? '#DDD6FE' : tone.bg,
    ...(isFirstColumn ? { boxShadow: `inset 4px 0 0 ${tone.border}` } : {}),
  };
};

const getDecisionReasonBadgeClass = (reason?: string | null, selected = false) => {
  const text = String(reason || '').toLocaleLowerCase('tr-TR');
  if (selected) return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  if (text.includes('spot')) return 'bg-amber-100 text-amber-800 border-amber-200';
  if (text.includes('maliyet')) return 'bg-blue-100 text-blue-800 border-blue-200';
  if (text.includes('bolge') || text.includes('tercih')) return 'bg-violet-100 text-violet-800 border-violet-200';
  if (text.includes('atandigi') || text.includes('limiti') || text.includes('uyumlu degil') || text.includes('uygunlugu')) return 'bg-rose-100 text-rose-800 border-rose-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const DECISION_REASON_BADGE_LEGEND = [
  { label: 'Secilen ozmal nedeni', className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  { label: 'Spot secildi / spot avantaji', className: 'bg-amber-100 text-amber-800 border-amber-200' },
  { label: 'Maliyet bazli nedenler', className: 'bg-blue-100 text-blue-800 border-blue-200' },
  { label: 'Bolge / tercih edilen teslim noktasi', className: 'bg-violet-100 text-violet-800 border-violet-200' },
  { label: 'Limit / uygun degil / daha once atanmis', className: 'bg-rose-100 text-rose-800 border-rose-200' },
  { label: 'Diger', className: 'bg-slate-100 text-slate-700 border-slate-200' },
] as const;

const getDecisionCriteriaBadgeClass = (value?: boolean | null) => {
  if (value == null) return 'bg-slate-100 text-slate-600 border-slate-200';
  return value ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-rose-100 text-rose-800 border-rose-200';
};

const getOwnAlternativeCandidates = (item: DecisionSupportPreview['selectedScenario']['decisions'][number]) => (
  Array.isArray(item.ownAlternativeExplanations)
    ? [...item.ownAlternativeExplanations].sort((a, b) => Number(b.selected) - Number(a.selected) || Number(a.selectedTotalCost || 0) - Number(b.selectedTotalCost || 0))
    : []
);

const toExcelColor = (hex: string) => hex.replace('#', '').toUpperCase();

const getCurrentDateInputValue = () => {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const getCurrentTimeInputValue = () => '00:00';
const DECISION_SUPPORT_AVERAGE_SPEED_KMH = 75;

const normalizeTimeInputValue = (value?: string | null) => {
  const text = String(value || '').trim();
  const match = text.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return '';
  return `${match[1].padStart(2, '0')}:${match[2]}`;
};

const extractLoadingStartTimeFromNotes = (notes: string[]) => {
  for (const note of notes) {
    const text = String(note || '').toLocaleLowerCase('tr-TR');
    if (!text || (!text.includes('yuklem') && !text.includes('yüklem'))) continue;

    const hhmmMatch = text.match(/(\d{1,2})[:.](\d{2})/);
    if (hhmmMatch) {
      const hours = Number(hhmmMatch[1]);
      const minutes = Number(hhmmMatch[2]);
      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      }
    }

    const hourOnlyMatch = text.match(/(?:saat\s*)?(\d{1,2})(?:'?(?:den|dan))?\s*sonra/);
    if (hourOnlyMatch) {
      const hours = Number(hourOnlyMatch[1]);
      if (hours >= 0 && hours <= 23) {
        return `${String(hours).padStart(2, '0')}:00`;
      }
    }
  }
  return getCurrentTimeInputValue();
};

const toRadians = (value: number) => (value * Math.PI) / 180;

const estimateAirDistanceKm = (originLat?: number | null, originLon?: number | null, destLat?: number | null, destLon?: number | null) => {
  if (originLat == null || originLon == null || destLat == null || destLon == null) return 0;
  const earthRadiusKm = 6371;
  const dLat = toRadians(destLat - originLat);
  const dLon = toRadians(destLon - originLon);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
    + Math.cos(toRadians(originLat)) * Math.cos(toRadians(destLat)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusKm * c;
};

const getDateInputParts = (value: Date) => {
  const pad = (item: number) => String(item).padStart(2, '0');
  return {
    date: `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`,
    time: `${pad(value.getHours())}:${pad(value.getMinutes())}`,
  };
};

const formatDurationMinutesLabel = (value: number) => {
  const totalMinutes = Math.max(0, Math.round(Number(value || 0)));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0 && minutes > 0) return `${hours} sa ${minutes} dk`;
  if (hours > 0) return `${hours} sa`;
  return `${minutes} dk`;
};

const formatDateTimeLabel = (date?: string, time?: string) => {
  if (!date || !time) return '-';
  const value = new Date(`${date}T${time}`);
  if (Number.isNaN(value.getTime())) return `${date} ${time}`;
  return `${value.toLocaleDateString('tr-TR')} ${value.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;
};

const formatDateTimeValue = (value?: string | null) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatTurkeyDate = (value?: string | null) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString('tr-TR', { timeZone: 'Europe/Istanbul' });
};

const formatTurkeyTime = (value?: string | null) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleTimeString('tr-TR', { timeZone: 'Europe/Istanbul', hour: '2-digit', minute: '2-digit' });
};

const getStopWindowMismatchReason = (stop?: { arrivalAt?: string; acceptanceWindowLabel?: string | null; windowMatched?: boolean } | null) => {
  if (!stop || stop.windowMatched || !stop.acceptanceWindowLabel || stop.acceptanceWindowLabel === '-') return null;
  const arrivalText = formatDateTimeValue(stop.arrivalAt);
  return `${stop.acceptanceWindowLabel} penceresi, varis ${arrivalText}`;
};

const getBackwardPlannedAssignmentStart = (shipment: ParsedShipment) => {
  const firstStop = shipment.stops[0];
  const acceptanceStartTime = normalizeTimeInputValue(shipment.deliveryAcceptanceStartTime || firstStop?.acceptanceStartTime);
  if (!shipment.assignmentStartDate || !acceptanceStartTime) return null;
  const targetArrivalAt = new Date(`${shipment.assignmentStartDate}T${acceptanceStartTime}`);
  if (Number.isNaN(targetArrivalAt.getTime())) return null;
  const firstLegKm = estimateAirDistanceKm(
    shipment.pickupLatitude,
    shipment.pickupLongitude,
    shipment.deliveryLatitude ?? firstStop?.latitude ?? null,
    shipment.deliveryLongitude ?? firstStop?.longitude ?? null,
  );
  const estimatedRoadKm = firstLegKm > 0 ? firstLegKm * 1.2 : 0;
  const driveMinutes = Math.round((estimatedRoadKm / DECISION_SUPPORT_AVERAGE_SPEED_KMH) * 60);
  const pickupServiceMinutes = Math.max(0, Number(shipment.pickupServiceDurationMinutes || 60));
  const suggestedStartAt = new Date(targetArrivalAt.getTime() - ((driveMinutes + pickupServiceMinutes) * 60 * 1000));
  return getDateInputParts(suggestedStartAt);
};

const getBackwardPlannedAssignmentExplanation = (shipment: ParsedShipment) => {
  const firstStop = shipment.stops[0];
  const acceptanceStartTime = normalizeTimeInputValue(shipment.deliveryAcceptanceStartTime || firstStop?.acceptanceStartTime);
  if (!shipment.assignmentStartDate || !acceptanceStartTime) return null;
  const targetArrivalAt = new Date(`${shipment.assignmentStartDate}T${acceptanceStartTime}`);
  if (Number.isNaN(targetArrivalAt.getTime())) return null;
  const firstLegKm = estimateAirDistanceKm(
    shipment.pickupLatitude,
    shipment.pickupLongitude,
    shipment.deliveryLatitude ?? firstStop?.latitude ?? null,
    shipment.deliveryLongitude ?? firstStop?.longitude ?? null,
  );
  const estimatedRoadKm = firstLegKm > 0 ? firstLegKm * 1.2 : 0;
  const driveMinutes = Math.round((estimatedRoadKm / DECISION_SUPPORT_AVERAGE_SPEED_KMH) * 60);
  const pickupServiceMinutes = Math.max(0, Number(shipment.pickupServiceDurationMinutes || 60));
  const suggested = getBackwardPlannedAssignmentStart(shipment);
  if (!suggested) return null;
  return `${formatDateTimeLabel(shipment.assignmentStartDate, acceptanceStartTime)} ilk bosaltma baslangici - ${formatDurationMinutesLabel(pickupServiceMinutes)} yukleme servis - ${formatDurationMinutesLabel(driveMinutes)} tahmini surus = ${formatDateTimeLabel(suggested.date, suggested.time)} baslangic`;
};

const combineAssignmentStart = (date?: string, time?: string) => {
  if (!date) return null;
  return `${date} ${time || '00:00'}:00`;
};

const mapOpenShipmentToParsedShipment = (shipment: any, warehouse: any): ParsedShipment => {
  const assignmentStartDate = getCurrentDateInputValue();
  const assignmentStartTime = getCurrentTimeInputValue();
  return {
    arac_no: shipment.plan_code || shipment.shipment_no || `OPEN-${shipment.id}`,
    planCode: shipment.plan_code || shipment.shipment_no || `OPEN-${shipment.id}`,
    sourceShipmentId: shipment.id ? String(shipment.id) : undefined,
    assignmentStartDate,
    assignmentStartTime,
    pickupDate: shipment.pickup_date || combineAssignmentStart(assignmentStartDate, assignmentStartTime),
    pickupWarehouseId: String(shipment.pickup_warehouse_id || warehouse?.id || ''),
    pickupWarehouseName: shipment.pickup_warehouse_name || warehouse?.name || '-',
    pickupCity: shipment.pickup_city || warehouse?.city || '',
    pickupDistrict: shipment.pickup_district || warehouse?.district || '',
    pickupAddress: shipment.pickup_address || warehouse?.address || '',
    pickupLatitude: shipment.pickup_latitude != null ? Number(shipment.pickup_latitude) : warehouse?.latitude ? Number(warehouse.latitude) : null,
    pickupLongitude: shipment.pickup_longitude != null ? Number(shipment.pickup_longitude) : warehouse?.longitude ? Number(warehouse.longitude) : null,
    pickupServiceDurationMinutes: 60,
    deliveryCustomerCode: shipment.delivery_customer_id ? String(shipment.delivery_customer_id) : '',
    deliveryCustomerName: shipment.delivery_name || '-',
    deliveryCustomerCity: shipment.delivery_city || '',
    deliveryCustomerDistrict: shipment.delivery_district || '',
    deliveryChannelType: '',
    deliveryLatitude: shipment.delivery_latitude != null ? Number(shipment.delivery_latitude) : null,
    deliveryLongitude: shipment.delivery_longitude != null ? Number(shipment.delivery_longitude) : null,
    deliveryAcceptanceStartTime: null,
    deliveryAcceptanceEndTime: null,
    deliveryServiceDurationMinutes: 60,
    deliveryWaitDurationMinutes: 0,
    stopCount: 1,
    stops: [{
      customerCode: shipment.delivery_customer_id ? String(shipment.delivery_customer_id) : '',
      customerName: shipment.delivery_name || '-',
      city: shipment.delivery_city || '',
      district: shipment.delivery_district || '',
      volume: Number(shipment.volume_m3 || 0),
      loadType: shipment.load_type || '',
      tonnage: Number(shipment.weight_kg || 0),
      existingCustomerId: shipment.delivery_customer_id ? String(shipment.delivery_customer_id) : undefined,
      latitude: shipment.delivery_latitude != null ? Number(shipment.delivery_latitude) : null,
      longitude: shipment.delivery_longitude != null ? Number(shipment.delivery_longitude) : null,
      address: shipment.delivery_address || '',
      channelType: '',
      acceptanceStartTime: null,
      acceptanceEndTime: null,
      serviceDurationMinutes: 60,
      waitDurationMinutes: 0,
    }],
    totalVolume: Number(shipment.volume_m3 || 0),
    totalTonnage: Number(shipment.weight_kg || 0),
    loadTypes: shipment.load_type ? [shipment.load_type] : [],
    vehicleType: shipment.vehicle_spec_type || 'Tır Tenteli Kayar Perde',
    notes: [String(shipment.notes || '').trim()].filter(Boolean),
    existingCustomerId: shipment.delivery_customer_id ? String(shipment.delivery_customer_id) : undefined,
    excludeFromKds: false,
  };
};

const handleHorizontalScrollKeyDown = (event: any) => {
  const container = event.currentTarget as HTMLDivElement;
  if (!container) return;
  if (event.key === 'ArrowRight') {
    event.preventDefault();
    container.scrollBy({ left: 300 });
  } else if (event.key === 'ArrowLeft') {
    event.preventDefault();
    container.scrollBy({ left: -300 });
  } else if (event.key === 'Home') {
    event.preventDefault();
    container.scrollTo({ left: 0 });
  } else if (event.key === 'End') {
    event.preventDefault();
    container.scrollTo({ left: container.scrollWidth });
  }
};

const SCROLL_WRAPPER_STYLE: CSSProperties = {
  overflowX: 'auto',
  scrollbarGutter: 'stable',
  WebkitOverflowScrolling: 'touch',
};

const parseValidDate = (value?: string | null) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatTimelineDateTime = (value?: string | null) => {
  const parsed = parseValidDate(value);
  return parsed ? parsed.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-';
};

const addMinutesToDate = (date: Date, minutes: number) => new Date(date.getTime() + (Math.max(0, minutes) * 60000));

export function BulkShipmentImportModal({ isOpen, onClose, onCreated }: { isOpen: boolean; onClose: () => void; onCreated?: () => void }) {
  const { showAlert } = useAppAlert();
  const [step, setStep] = useState<'upload' | 'warehouse' | 'preview' | 'creating'>('upload');
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState<any>(null);
  const [decisionDistanceProvider, setDecisionDistanceProvider] = useState<'bms' | 'google_maps' | 'openrouteservice' | 'osm'>('osm');
  const [excelRows, setExcelRows] = useState<ExcelRow[]>([]);
  const [detailRows, setDetailRows] = useState<DetailExcelRow[]>([]);
  const [parsedShipments, setParsedShipments] = useState<ParsedShipment[]>([]);
  const [customerStatuses, setCustomerStatuses] = useState<Record<string, ValidationStatus>>({});
  const [creating, setCreating] = useState(false);
  const [createErrors, setCreateErrors] = useState<string[]>([]);
  const [autoDecisionSupport, setAutoDecisionSupport] = useState(false);
  const [includeOpenUnassignedInPlanTable, setIncludeOpenUnassignedInPlanTable] = useState(false);
  const [ignoreVehicleStatusForMatchingDepot, setIgnoreVehicleStatusForMatchingDepot] = useState(false);
  const [decisionDistanceLimitKm, setDecisionDistanceLimitKm] = useState(50);
  const [decisionSearchMethod, setDecisionSearchMethod] = useState<'random_search' | 'kds' | 'kds_plus_kds'>('kds');
  const [decisionReplicationCount, setDecisionReplicationCount] = useState(20);
  const [decisionPreviewLoading, setDecisionPreviewLoading] = useState(false);
  const [decisionPreview, setDecisionPreview] = useState<DecisionSupportPreview | null>(null);
  const [selectedDecisionKey, setSelectedDecisionKey] = useState<string | null>(null);
  const [decisionPreviewJobId, setDecisionPreviewJobId] = useState<string | null>(null);
  const [decisionPreviewJob, setDecisionPreviewJob] = useState<DecisionPreviewJobSnapshot | null>(null);
  const [decisionEstimate, setDecisionEstimate] = useState<DecisionSupportEstimate | null>(null);
  const [decisionEstimateLoading, setDecisionEstimateLoading] = useState(false);
  const [previewTab, setPreviewTab] = useState<'candidates' | 'plans' | 'results'>('candidates');
  const [alignAssignmentStartBackwardFromDeliveryWindow, setAlignAssignmentStartBackwardFromDeliveryWindow] = useState(false);
  const originalAssignmentStartDatesRef = useRef<Map<string, {date: string; time: string}>>(new Map());
  const [showDecisionTimeline, setShowDecisionTimeline] = useState(false);
  const [decisionTimelineZoom, setDecisionTimelineZoom] = useState(1);
  const decisionTimelineScrollRef = useRef<HTMLDivElement | null>(null);
  const decisionTimelineDragRef = useRef<{ startX: number; startScrollLeft: number; dragging: boolean } | null>(null);
  const kdsIncludedShipments = useMemo(() => parsedShipments.filter((shipment) => !shipment.excludeFromKds), [parsedShipments]);
  const candidateVehicleRows = useMemo(() => decisionEstimate?.candidateVehicles || [], [decisionEstimate]);
  const previewResultStopColumnCount = useMemo(() => {
    const decisions = decisionPreview?.selectedScenario?.decisions || [];
    return decisions.reduce((maxCount, item) => Math.max(maxCount, getDisplayedStopSchedules(item).length), 0);
  }, [decisionPreview]);
  const [assignmentApproval, setAssignmentApproval] = useState<Record<string, boolean>>({});
  const decisionTimelineRows = useMemo(() => {
    const decisions = decisionPreview?.selectedScenario?.decisions || [];
    return decisions
      .map((item) => {
        const displayedOwn = getDisplayedOwnAlternative(item);
        const stopSchedules = getDisplayedStopSchedules(item);
        const assignmentStart = parseValidDate(item.assignmentStartTime || item.estimatedPickupArrivalAt);
        const pickupArrival = parseValidDate(item.estimatedPickupArrivalAt);
        const finishAt = parseValidDate(item.estimatedRouteFinishAt);
        const hubArrival = parseValidDate(item.estimatedNearestHubArrivalAt);
        const pickupServiceMinutes = Math.max(0, Number(displayedOwn?.pickupServiceDurationMinutes || item.spotAlternative?.pickupServiceDurationMinutes || 0));
        const pickupServiceEnd = pickupArrival ? addMinutesToDate(pickupArrival, pickupServiceMinutes) : null;
        const stopEvents = stopSchedules
          .map((stop) => ({
            label: stop.label || `Durak ${stop.sequenceNo}`,
            arrivalAt: stop.arrivalAt,
            serviceStartAt: stop.serviceStartAt,
            serviceEndAt: stop.serviceEndAt,
          }))
          .filter((stop) => stop.arrivalAt || stop.serviceStartAt || stop.serviceEndAt);
        const segments: Array<{ key: string; kind: 'drive' | 'service' | 'wait' | 'rest'; label: string; start: string; end: string }> = [];
        const pushSegment = (key: string, kind: 'drive' | 'service' | 'wait' | 'rest', label: string, start?: Date | null, end?: Date | null) => {
          if (!start || !end) return;
          if (end.getTime() <= start.getTime()) return;
          segments.push({ key, kind, label, start: start.toISOString(), end: end.toISOString() });
        };

        pushSegment('assignment-drive', 'drive', 'Yüklemeye sürüş', assignmentStart, pickupArrival);
        pushSegment('pickup-service', 'service', 'Yükleme', pickupArrival, pickupServiceEnd);

        let cursor = pickupServiceEnd || pickupArrival;
        stopEvents.forEach((stop, index) => {
          const arrival = parseValidDate(stop.arrivalAt);
          const serviceStart = parseValidDate(stop.serviceStartAt);
          const serviceEnd = parseValidDate(stop.serviceEndAt);
          pushSegment(`drive-${index}`, 'drive', `${stop.label} sürüş`, cursor, arrival);
          pushSegment(`wait-${index}`, 'wait', `${stop.label} bekleme`, arrival, serviceStart);
          pushSegment(`service-${index}`, 'service', `${stop.label} boşaltma`, serviceStart, serviceEnd);
          cursor = serviceEnd || serviceStart || arrival || cursor;
        });

        pushSegment('hub-drive', 'drive', 'Huba dönüş', finishAt, hubArrival);

        const eventDates = [assignmentStart, pickupArrival, pickupServiceEnd, finishAt, hubArrival, ...stopEvents.flatMap((stop) => [parseValidDate(stop.arrivalAt), parseValidDate(stop.serviceStartAt), parseValidDate(stop.serviceEndAt)])].filter(Boolean) as Date[];
        if (eventDates.length === 0) return null;

        const structuredRouteMinutes = segments
          .filter((segment) => segment.kind !== 'drive' || segment.key !== 'hub-drive')
          .reduce((sum, segment) => sum + Math.max(0, Math.round((new Date(segment.end).getTime() - new Date(segment.start).getTime()) / 60000)), 0);
        const sourceAlternative: any = displayedOwn || item.spotAlternative || null;
        const declaredDriveMinutes = Math.round(Number(sourceAlternative?.totalDriveHours || 0) * 60);
        const declaredBreakMinutes = Math.round(Number(sourceAlternative?.totalBreakHours || 0) * 60);
        const declaredRestMinutes = Math.round(Number(sourceAlternative?.totalRestHours || 0) * 60);
        const declaredServiceMinutes = Math.round(Number(sourceAlternative?.totalServiceHours || 0) * 60);
        const declaredWaitingMinutes = Math.round(Number(sourceAlternative?.totalWaitingHours || 0) * 60);
        const declaredRouteMinutes = declaredDriveMinutes + declaredBreakMinutes + declaredRestMinutes + declaredServiceMinutes + declaredWaitingMinutes;
        const missingBreakRestMinutes = Math.max(0, declaredRouteMinutes - structuredRouteMinutes);
        const syntheticFinishAt = assignmentStart ? addMinutesToDate(assignmentStart, declaredRouteMinutes) : null;
        const effectiveFinishAt = [finishAt, syntheticFinishAt].filter(Boolean).sort((a, b) => b!.getTime() - a!.getTime())[0] || null;
        const hubOffsetMinutes = hubArrival && finishAt ? Math.max(0, Math.round((hubArrival.getTime() - finishAt.getTime()) / 60000)) : 0;
        const effectiveHubArrival = effectiveFinishAt ? addMinutesToDate(effectiveFinishAt, hubOffsetMinutes) : hubArrival;
        if (finishAt && effectiveFinishAt && effectiveFinishAt.getTime() > finishAt.getTime()) {
          pushSegment('break-rest-buffer', 'rest', 'Mola / Dinlenme', finishAt, effectiveFinishAt);
        }
        const warnings: string[] = [];
        if (missingBreakRestMinutes >= 15) warnings.push(`Mola/dinlenme icin ${missingBreakRestMinutes} dk zaman damgasi eksik`);
        if (assignmentStart && pickupArrival && pickupArrival.getTime() < assignmentStart.getTime()) warnings.push('Yüklemeye varış, atama başlangıcından önce görünüyor');
        if (finishAt && cursor && finishAt.getTime() < cursor.getTime()) warnings.push('Rota bitiş zamanı son boşaltmadan önce görünüyor');
        if (hubArrival && finishAt && hubArrival.getTime() < finishAt.getTime()) warnings.push('Huba varış, rota bitişinden önce görünüyor');

        return {
          key: `${item.planCode}-${item.shipmentId}`,
          planCode: item.planCode,
          vehicleLabel: item.decision === 'OZMAL' ? (item.assignedVehiclePlate || '-') : 'Spot',
          decisionLabel: item.decision === 'OZMAL' ? 'Özmal' : 'Spot',
          pickupLabel: item.pickupName || [item.pickupCity, item.pickupDistrict].filter(Boolean).join(' / ') || '-',
          deliveryLabel: item.deliveryName || [item.deliveryCity, item.deliveryDistrict].filter(Boolean).join(' / ') || '-',
          assignmentStart: assignmentStart ? assignmentStart.toISOString() : null,
          pickupArrival: pickupArrival ? pickupArrival.toISOString() : null,
          routeFinish: effectiveFinishAt ? effectiveFinishAt.toISOString() : null,
          hubArrival: effectiveHubArrival ? effectiveHubArrival.toISOString() : null,
          pickupServiceEnd: pickupServiceEnd ? pickupServiceEnd.toISOString() : null,
          stopEvents,
          segments,
          declaredDriveMinutes,
          declaredBreakMinutes,
          declaredRestMinutes,
          declaredServiceMinutes,
          declaredWaitingMinutes,
          missingBreakRestMinutes,
          warnings,
          startAt: new Date(Math.min(...eventDates.map((date) => date.getTime()))),
          endAt: new Date(Math.max(...[...eventDates.map((date) => date.getTime()), effectiveFinishAt?.getTime() || 0, effectiveHubArrival?.getTime() || 0])),
        };
      })
      .filter(Boolean)
      .sort((a: any, b: any) => a.startAt.getTime() - b.startAt.getTime());
  }, [decisionPreview]);
  const decisionTimelineWindow = useMemo(() => {
    if (!decisionTimelineRows.length) return null;
    const starts = decisionTimelineRows.map((row: any) => row.startAt.getTime());
    const ends = decisionTimelineRows.map((row: any) => row.endAt.getTime());
    return {
      start: new Date(Math.min(...starts)),
      end: new Date(Math.max(...ends)),
    };
  }, [decisionTimelineRows]);
  const decisionTimelineScale = useMemo(() => {
    if (!decisionTimelineWindow) return null;
    const start = new Date(decisionTimelineWindow.start);
    start.setMinutes(0, 0, 0);
    const end = new Date(decisionTimelineWindow.end);
    if (end.getMinutes() > 0 || end.getSeconds() > 0 || end.getMilliseconds() > 0) {
      end.setHours(end.getHours() + 1, 0, 0, 0);
    }
    const ticks: Date[] = [];
    const dayBands: Array<{ key: string; label: string; left: number; width: number }> = [];
    const cursor = new Date(start);
    while (cursor.getTime() <= end.getTime()) {
      ticks.push(new Date(cursor));
      cursor.setMinutes(cursor.getMinutes() + 30, 0, 0);
    }
    const totalHours = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (60 * 60 * 1000)));
    const baseHourWidth = 88;
    const hourWidth = Math.max(52, Math.round(baseHourWidth * decisionTimelineZoom));
    const width = Math.max(1200, totalHours * hourWidth);
    let bandStartIndex = 0;
    while (bandStartIndex < ticks.length) {
      const bandStart = ticks[bandStartIndex];
      const dateKey = bandStart.toLocaleDateString('tr-TR');
      let bandEndIndex = bandStartIndex;
      while (bandEndIndex + 1 < ticks.length && ticks[bandEndIndex + 1].toLocaleDateString('tr-TR') === dateKey) {
        bandEndIndex += 1;
      }
      const left = ((bandStart.getTime() - start.getTime()) / (60 * 60 * 1000)) * hourWidth;
      const endTick = ticks[Math.min(bandEndIndex + 1, ticks.length - 1)];
      const rawWidth = ((endTick.getTime() - bandStart.getTime()) / (60 * 60 * 1000)) * hourWidth;
      dayBands.push({
        key: `${dateKey}-${bandStartIndex}`,
        label: bandStart.toLocaleDateString('tr-TR', { weekday: 'short', day: '2-digit', month: '2-digit' }),
        left,
        width: Math.max(hourWidth, rawWidth),
      });
      bandStartIndex = bandEndIndex + 1;
    }
    return {
      start,
      end,
      ticks,
      dayBands,
      totalHours,
      hourWidth,
      width,
    };
  }, [decisionTimelineWindow, decisionTimelineZoom]);

  const handleDecisionTimelineDragStart = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!decisionTimelineScrollRef.current) return;
    decisionTimelineDragRef.current = {
      startX: event.clientX,
      startScrollLeft: decisionTimelineScrollRef.current.scrollLeft,
      dragging: true,
    };
  };

  const handleDecisionTimelineDragMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!decisionTimelineDragRef.current?.dragging || !decisionTimelineScrollRef.current) return;
    const delta = event.clientX - decisionTimelineDragRef.current.startX;
    decisionTimelineScrollRef.current.scrollLeft = decisionTimelineDragRef.current.startScrollLeft - delta;
  };

  const handleDecisionTimelineDragEnd = () => {
    if (decisionTimelineDragRef.current) decisionTimelineDragRef.current.dragging = false;
  };

  useEffect(() => {
    if (decisionPreview?.selectedScenario?.decisions) {
      const initial: Record<string, boolean> = {};
      for (const d of decisionPreview.selectedScenario.decisions) {
        initial[d.planCode] = true;
      }
      setAssignmentApproval(initial);
    }
  }, [decisionPreview]);

  useEffect(() => {
    if (!decisionPreview?.bmsLogs?.length) return;
    console.groupCollapsed('[Decision Support] BMS Requests');
    console.table(decisionPreview.bmsLogs);
    console.groupEnd();
  }, [decisionPreview]);

  useEffect(() => {
    if (!isOpen) return;
    setStep('upload');
    setExcelRows([]);
    setDetailRows([]);
    setParsedShipments([]);
    setCustomerStatuses({});
    setSelectedWarehouseId('');
    setSelectedWarehouse(null);
    setDecisionDistanceProvider('osm');
    setCreateErrors([]);
    setAutoDecisionSupport(false);
    setIncludeOpenUnassignedInPlanTable(false);
    setIgnoreVehicleStatusForMatchingDepot(false);
    setDecisionDistanceLimitKm(50);
    setDecisionSearchMethod('kds');
    setDecisionReplicationCount(20);
    setDecisionPreview(null);
    setDecisionPreviewLoading(false);
    setSelectedDecisionKey(null);
    setDecisionPreviewJobId(null);
    setDecisionPreviewJob(null);
    setDecisionEstimate(null);
    setDecisionEstimateLoading(false);
    setPreviewTab('candidates');
    setAlignAssignmentStartBackwardFromDeliveryWindow(false);
    setShowDecisionTimeline(false);

    warehouseService.getWarehouses().then((res: any) => {
      setWarehouses(Array.isArray(res?.data) ? res.data : []);
    }).catch(() => setWarehouses([]));
  }, [isOpen]);

  const handleLoadOnlyOpenUnassigned = async () => {
    try {
      const defaultWarehouse = warehouses[0] || null;
      setParsedShipments([]);
      setExcelRows([]);
      setDetailRows([]);
      setSelectedWarehouse(defaultWarehouse);
      setSelectedWarehouseId(defaultWarehouse ? String(defaultWarehouse.id) : '');
      setAutoDecisionSupport(true);
      toast.success('Yükleme noktası seçip atanmamış açık siparişlerle karar destek çalıştırabilirsiniz');
      setStep('warehouse');
    } catch (error) {
      console.error('Open unassigned fetch error:', error);
      toast.error('Atanmamış sevkiyatlar yüklenemedi');
    }
  };

  const cancelDecisionPreviewJob = async (jobId?: string | null) => {
    if (!jobId) return;
    try {
      await supplyService.cancelPreviewDecisionSupportImport(jobId);
    } catch (error: any) {
      const statusCode = error?.statusCode || error?.status;
      if (statusCode === 404) return;
    }
  };

  const handleModalClose = () => {
    void cancelDecisionPreviewJob(decisionPreviewJobId);
    setDecisionPreviewLoading(false);
    onClose();
  };

  useEffect(() => {
    if (isOpen || !decisionPreviewJobId) return;
    void cancelDecisionPreviewJob(decisionPreviewJobId);
    setDecisionPreviewLoading(false);
    setDecisionPreviewJob(null);
    setDecisionPreviewJobId(null);
  }, [isOpen, decisionPreviewJobId]);

  useEffect(() => {
    if (step === 'preview' && autoDecisionSupport) return;
    void cancelDecisionPreviewJob(decisionPreviewJobId);
    setDecisionPreview(null);
    setDecisionPreviewLoading(false);
    setDecisionPreviewJob(null);
    setDecisionPreviewJobId(null);
  }, [autoDecisionSupport, step]);

  const formatDurationLabel = (durationMs?: number) => {
    const totalSeconds = Math.max(0, Math.round(Number(durationMs || 0) / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes > 0 ? `${minutes} dk ${seconds} sn` : `${seconds} sn`;
  };

  const downloadTemplate = () => {
    const worksheet1 = XLSX.utils.json_to_sheet([
      {
        ARAÇ_NO: 'A06-275',
        ALICI_KODU: '211-120.00.02.01.009.001',
        ALICI_ADI: 'BAKİ ÖZKILIÇ',
        İL: 'Adana',
        İLÇE: 'Kozan',
        'YÜK TÜRÜ': 'Siyah Çay',
        M3: 23.4,
        'Brüt KG': 905,
        'ARAÇ TİPİ': 'TIR',
        NOT: '',
      },
      {
        ARAÇ_NO: 'A06-275',
        ALICI_KODU: '211-120.00.02.80.001.01',
        ALICI_ADI: 'AYSANLAR GIDA OSMANİYE',
        İL: 'Osmaniye',
        İLÇE: 'Merkez',
        'YÜK TÜRÜ': 'Siyah Çay',
        M3: 60.0,
        'Brüt KG': 2200,
        'ARAÇ TİPİ': '',
        NOT: '',
      },
    ]);
    const worksheet2 = XLSX.utils.json_to_sheet([
      {
        ARAÇ_NO: 'A06-277',
        SIPARIS_KODU: 'SP001',
        'SİPARİŞ BELGE NO': '46184',
        ÇIKIŞ_TARİHİ: '10.06.2026',
        GİRİŞ_TARİHİ: '11.06.2026',
        MALZEME_KODU: 'C.D.1.1.07.0500.12Q',
        MALZEME_ADI: 'BERGAMOT AROMALI SİYAH ÇAY 500 GR 12 LI MET.Q',
        SIPARIS_MIKTARI: 10,
      },
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet1, 'ARAÇ PLANI');
    XLSX.utils.book_append_sheet(workbook, worksheet2, 'Sevk Plan');
    XLSX.writeFile(workbook, 'toplu-siparis-ornek.xlsx');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });

        // Scan sheets in order; first two with ARAC_NO/ARAÇ_NO header = plan, then detail
        let planSheetIdx = -1;
        let detailSheetIdx = -1;
        for (let i = 0; i < workbook.SheetNames.length; i++) {
          const sheetName = workbook.SheetNames[i];
          const sheet = workbook.Sheets[sheetName];
          const ref = sheet?.['!ref'];
          if (!ref) continue;
          const refRange = XLSX.utils.decode_range(ref);
          if (refRange.e.r < 0) continue;
          const maxRow = Math.min(refRange.e.r, 30);
          const peek: any[][] = XLSX.utils.sheet_to_json(sheet, { defval: '', header: 1, range: { s: { r: 0, c: 0 }, e: { r: maxRow, c: 30 } } });
          const found = peek.some((r) => { const c = String(r[0] ?? '').trim().toLocaleLowerCase('tr-TR'); return c === 'arac_no' || c === 'araç_no'; });
          if (found) {
            if (planSheetIdx < 0) planSheetIdx = i;
            else { detailSheetIdx = i; break; }
          }
        }
        if (planSheetIdx < 0) { toast.error('Excel dosyasında plan sayfası bulunamadı'); return; }

        const planSheetName = workbook.SheetNames[planSheetIdx];
        const detailSheetName = detailSheetIdx >= 0 ? workbook.SheetNames[detailSheetIdx] : undefined;
        const isGuncelPlan = String(planSheetName).trim().toLocaleLowerCase('tr-TR') === 'sayfa1';
        const planMatrix: any[][] = XLSX.utils.sheet_to_json(workbook.Sheets[planSheetName], { defval: '', header: 1 });

        // Only parse detail sheet if it has ALICI_KODU/ALICI column (peek first row)
        let detailRawRows: any[] = [];
        if (detailSheetName) {
          const detailHeader: any[][] = XLSX.utils.sheet_to_json(workbook.Sheets[detailSheetName], { defval: '', header: 1, range: { s: { r: 0, c: 0 }, e: { r: 0, c: 20 } } });
          const firstRow: any[] = detailHeader[0] || [];
          if (firstRow.some((v) => { const k = String(v).trim().toLocaleLowerCase('tr-TR'); return k === 'alici_kodu' || k === 'alici'; })) {
            detailRawRows = XLSX.utils.sheet_to_json(workbook.Sheets[detailSheetName], { defval: '' });
          }
        }

        // Build column map from header row (supports both aksaray.xlsx and guncelplan.xlsx)
        const headerRowIndex = planMatrix.findIndex((row) => { const c = String(row[0] ?? '').trim().toLocaleLowerCase('tr-TR'); return c === 'arac_no' || c === 'araç_no'; });
        const dataStartIndex = headerRowIndex >= 0 ? headerRowIndex + 1 : (isGuncelPlan ? 3 : 2);
        const headerRow: any[] = headerRowIndex >= 0 ? planMatrix[headerRowIndex] : [];
        const col = (keys: string[]) => {
          const idx = headerRow.findIndex((v) => keys.some((k) => String(v).trim().toLocaleLowerCase('tr-TR') === k));
          return idx >= 0 ? idx : undefined;
        };
        const colPriority = (groups: string[][]) => {
          for (const keys of groups) {
            const idx = col(keys);
            if (idx != null) return idx;
          }
          return undefined;
        };
        const colMap = {
          aliciKodu: col(['alici_kodu', 'alici']),
          aliciAdi: col(['alici_adi']),
          sehir: col(['sehir', 'sehır', 'il', 'i̇l']),
          ilce: col(['ilçe', 'i̇lçe', 'ilce']),
          yukturu: colPriority([
            ['yük türü', 'yük_türü', 'yuk türü', 'yuk_turu', 'yüktürü'],
            ['ürün grubu', 'urun grubu', 'urun_grubu', 'ürün_grubu'],
            ['yükleme tipi', 'yükleme ti̇pi̇', 'yukleme_tipi'],
          ]),
          hacim: col(['araç hacim', 'araç haci̇m', 'm3']),
          tonaj: col(['brüt kg', 'brut kg']),
          aracTipi: col(['araç tipi', 'araç ti̇pi̇']),
          not: col(['not']),
        };
        const planRows = planMatrix.slice(dataStartIndex).map((row) => row.map((cell) => String(cell ?? '').trim()));
        let currentAracNo = '';
        let currentVehicleType = isGuncelPlan ? 'TIR' : 'Tır Tenteli Kayar Perde';
        let currentNote = '';
        let currentYukturu = '';
        let currentTonaj = 5;
        const mapped: ExcelRow[] = planRows.map((row) => {
          if (row[0]) currentAracNo = row[0];
          if (colMap.aracTipi != null && row[colMap.aracTipi]) currentVehicleType = row[colMap.aracTipi];
          else if (colMap.hacim != null && row[colMap.hacim]) currentVehicleType = row[colMap.hacim];
          if (colMap.yukturu != null && row[colMap.yukturu]) currentYukturu = row[colMap.yukturu];
          if (colMap.tonaj != null && row[colMap.tonaj]) currentTonaj = Number(String(row[colMap.tonaj] || '0').replace(',', '.')) || currentTonaj || 5;
          currentNote = colMap.not != null ? (row[colMap.not] || '') : '';
          return {
            arac_no: currentAracNo,
            alici_kodu: colMap.aliciKodu != null ? row[colMap.aliciKodu] || '' : row[1] || '',
            alici_adi: colMap.aliciAdi != null ? row[colMap.aliciAdi] || '' : row[2] || '',
            sehir: colMap.sehir != null ? row[colMap.sehir] || '' : row[3] || '',
            ilce: colMap.ilce != null ? row[colMap.ilce] || '' : row[4] || '',
            arac_tipi: currentVehicleType || 'TIR',
            not: currentNote,
            yukturu: currentYukturu || '',
            tonaj: currentTonaj || 5,
          };
        });

        const mappedDetail: DetailExcelRow[] = detailRawRows.map((row) => ({
          arac_no: String(row['ARAC_NO'] || row['ARAÇ_NO'] || '').trim(),
          siparis_kodu: String(row['SIPARIS_KODU'] || row['SIPARIS NO'] || row['SIPARIŞ NO'] || '').trim(),
          alici_kodu: String(row['ALICI_KODU'] || row['ALICI'] || '').trim(),
          alici_adi: String(row['ALICI_ADI'] || '').trim(),
          sehir: String(row['İL'] || row['IL'] || row['SEHIR'] || row['SEHİR'] || '').trim(),
          ilce: String(row['İLÇE'] || row['ILCE'] || '').trim(),
          m3toplam: Number(String(row['M3TOPLAM'] || row['TOPLAM_VOLUME'] || 0).replace(',', '.')) || 0,
          yukleme_tipi: String(row['YÜKLEME TİPİ'] || row['Yükleme Tipi'] || row['YUKLEME_TIPI'] || '').trim(),
          palet: Number(String(row['PALET'] || row['ARAÇ PALET'] || 0).replace(',', '.')) || 0,
          not: String(row['not'] || row['NOT'] || row['SIPARIS_NOTU'] || '').trim(),
        }));

        const filtered = mapped.filter((r) => r.arac_no && (r.alici_kodu || r.alici_adi));
        let filteredDetail = mappedDetail.filter((r) => r.arac_no && (r.alici_kodu || r.alici_adi));
        if (filtered.length === 0) {
          toast.error('Excel dosyasında geçerli satır bulunamadı');
          return;
        }
        if (filteredDetail.length === 0) {
          filteredDetail = filtered.map((r) => ({
            arac_no: r.arac_no,
            siparis_kodu: '',
            alici_kodu: r.alici_kodu,
            alici_adi: r.alici_adi,
            sehir: r.sehir,
            ilce: r.ilce,
            m3toplam: 0,
            yukleme_tipi: r.yukturu || '',
            palet: 0,
            not: r.not || '',
          }));
        }

        setExcelRows(filtered);
        setDetailRows(filteredDetail);
        toast.success(`${filtered.length} teslim satırı ve ${filteredDetail.length} detay satırı okundu`);
        setStep('warehouse');
      } catch (error) {
        console.error('Excel parse error:', error);
        toast.error('Excel dosyası okunamadı');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleWarehouseSelect = async () => {
    if (!selectedWarehouseId) {
      toast.error('Yükleme noktası (depo) seçin');
      return;
    }
    const wh = warehouses.find((w) => String(w.id) === selectedWarehouseId);
    setSelectedWarehouse(wh);

    const customerRefs = [...new Map(excelRows.map((row) => [`${normalizeCustomerMatchValue(row.alici_kodu)}__${normalizeCustomerMatchValue(row.alici_adi)}`, { code: row.alici_kodu, name: row.alici_adi }])).values()];
    const existingCustomers: any[] = await customerService.getCustomers().then((res: any) => Array.isArray(res?.data) ? res.data : []).catch(() => []);

    const statuses: Record<string, ValidationStatus> = {};
    const matched: Record<string, any> = {};
    const missingRefs: typeof customerRefs = [];
    for (const ref of customerRefs) {
      const codeKey = ref.code || ref.name;
      const warehouseByCode = findExistingWarehouseByCode(warehouses, ref.code);
      if (warehouseByCode) {
        const warehouseMatch = mapWarehouseToCustomerLike(warehouseByCode, ref.code, ref.name);
        statuses[codeKey] = 'warehouse';
        if (ref.code) matched[ref.code] = warehouseMatch;
        if (ref.name) matched[ref.name] = warehouseMatch;
        continue;
      }

      const found = findExistingCustomer(existingCustomers, ref.code, ref.name);
      if (found) {
        statuses[codeKey] = 'ok';
        let enriched = found;
        if (!found.latitude) {
          const foundWarehouse = findExistingWarehouseByCode(warehouses, ref.code);
          if (foundWarehouse && foundWarehouse.latitude != null) {
            enriched = { ...found, latitude: Number(foundWarehouse.latitude), longitude: Number(foundWarehouse.longitude) };
          }
        }
        matched[ref.code] = enriched;
        if (ref.name) matched[ref.name] = enriched;
      } else {
        statuses[codeKey] = 'missing';
        missingRefs.push(ref);
      }
    }

    if (missingRefs.length > 0) {
      toast.info(`${missingRefs.length} müşteri Stokbar'dan aranıyor...`);
      const stokbarResults = await Promise.allSettled(
        missingRefs.map((ref) =>
          customerService.fetchFromStokbarByCode(ref.code).then((res: any) => ({
            ref,
            customer: res?.data?.data,
          }))
        )
      );
      for (const result of stokbarResults) {
        if (result.status === 'fulfilled' && result.value.customer) {
          const { ref, customer } = result.value;
          existingCustomers.push(customer);
          const codeKey = ref.code || ref.name;
          statuses[codeKey] = 'ok';
          matched[ref.code] = customer;
          if (ref.name) matched[ref.name] = customer;
        }
      }
    }

    // Geocode fallback for stops without coordinates
    const placesWithoutCoords = [...new Map(
      excelRows
        .filter((row) => {
          const m = matched[row.alici_kodu] || matched[row.alici_adi];
          return !m || !m.latitude;
        })
        .map((row) => [`${row.sehir}|${row.ilce}`, { city: row.sehir, district: row.ilce }])
    ).values()];
    if (placesWithoutCoords.length > 0) {
      try {
        const geoResponse = await apiService.post('/geocode/batch', { places: placesWithoutCoords });
        if (geoResponse?.success && Array.isArray(geoResponse.data)) {
          for (const geo of geoResponse.data) {
            if (geo.latitude != null) {
              const key = `${geo.city}|${geo.district}`;
              for (const row of excelRows) {
                if (`${row.sehir}|${row.ilce}` !== key) continue;
                const entry = matched[row.alici_kodu] || matched[row.alici_adi];
                if (entry && !entry.latitude) {
                  entry.latitude = geo.latitude;
                  entry.longitude = geo.longitude;
                }
              }
            }
          }
        }
      } catch {
        // Geocode fallback is best-effort
      }
    }

    setCustomerStatuses(statuses);

    const shipments = parseShipments(excelRows, detailRows, selectedWarehouseId, wh, matched);
    let openUnassignedShipments: ParsedShipment[] = [];
    if (includeOpenUnassignedInPlanTable || excelRows.length === 0) {
      try {
        const response: any = await apiService.get('/shipments');
        const rows = Array.isArray(response?.data) ? response.data : [];
        openUnassignedShipments = rows
          .filter((item: any) => ['open', 'pending'].includes(String(item.status || '').toLowerCase()))
          .filter((item: any) => String(item.pickup_warehouse_id || '') === String(selectedWarehouseId))
          .filter((item: any) => !item.assigned_driver_id && !item.assigned_vehicle_id)
          .map((item: any) => mapOpenShipmentToParsedShipment(item, wh));
      } catch (error) {
        console.error('Open unassigned shipments fetch failed:', error);
      }
    }
    const mergedShipments = excelRows.length === 0
      ? openUnassignedShipments
      : includeOpenUnassignedInPlanTable
        ? [...shipments, ...openUnassignedShipments]
        : shipments;
    setParsedShipments(mergedShipments);
    if (excelRows.length === 0 && openUnassignedShipments.length > 0) {
      toast.success(`${openUnassignedShipments.length} açık atanmamış sevk plan tablosuna eklendi`);
    } else if (includeOpenUnassignedInPlanTable && openUnassignedShipments.length > 0) {
      toast.success(`${shipments.length} Excel sevkiyatina ek olarak ${openUnassignedShipments.length} acik atanmamis sevk plan tablosuna eklendi`);
    }
    setStep('preview');
  };

  useEffect(() => {
    if (step !== 'preview' || kdsIncludedShipments.length === 0) {
      setDecisionEstimate(null);
      setDecisionEstimateLoading(false);
      return;
    }
    let cancelled = false;
    setDecisionEstimateLoading(true);
    void supplyService.estimatePreviewDecisionSupportImport({ shipments: kdsIncludedShipments, distanceLimitKm: decisionDistanceLimitKm, distanceProvider: decisionDistanceProvider, ignoreVehicleStatusForMatchingDepot })
      .then((response: any) => {
        if (cancelled) return;
        setDecisionEstimate(response?.data || null);
      })
      .catch((error: any) => {
        if (cancelled) return;
        console.error('Decision support estimate error:', error);
        setDecisionEstimate(null);
      })
      .finally(() => {
        if (!cancelled) setDecisionEstimateLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [kdsIncludedShipments, decisionDistanceLimitKm, decisionDistanceProvider, ignoreVehicleStatusForMatchingDepot, step]);

  useEffect(() => {
    if (alignAssignmentStartBackwardFromDeliveryWindow) {
      if (originalAssignmentStartDatesRef.current.size === 0) {
        setParsedShipments((current) => current.map((shipment) => {
          originalAssignmentStartDatesRef.current.set(shipment.planCode || shipment.arac_no, {
            date: shipment.assignmentStartDate || '',
            time: shipment.assignmentStartTime || '',
          });
          const suggested = getBackwardPlannedAssignmentStart(shipment);
          if (!suggested) return shipment;
          return {
            ...shipment,
            assignmentStartDate: suggested.date,
            assignmentStartTime: suggested.time,
            pickupDate: combineAssignmentStart(suggested.date, suggested.time),
          };
        }));
      }
    } else {
      const origDates = originalAssignmentStartDatesRef.current;
      if (origDates.size > 0) {
        setParsedShipments((current) => current.map((shipment) => {
          const orig = origDates.get(shipment.planCode || shipment.arac_no);
          if (!orig || !orig.date) return shipment;
          return {
            ...shipment,
            assignmentStartDate: orig.date,
            assignmentStartTime: orig.time,
            pickupDate: combineAssignmentStart(orig.date, orig.time),
          };
        }));
        originalAssignmentStartDatesRef.current = new Map();
      }
    }
  }, [alignAssignmentStartBackwardFromDeliveryWindow]);

  const parseShipments = (rows: ExcelRow[], detailRowsInput: DetailExcelRow[], whId: string, wh: any, matchedCustomers: Record<string, any>): ParsedShipment[] => {
    const groups: Record<string, ExcelRow[]> = {};
    for (const row of rows) {
      const key = row.arac_no || 'unknown';
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    }

    const detailByAracAndCustomer = new Map<string, DetailExcelRow[]>();
    const detailByArac = new Map<string, DetailExcelRow[]>();
    for (const detail of detailRowsInput) {
      const aracKey = detail.arac_no;
      const customerKey = `${detail.arac_no}__${detail.alici_kodu || detail.alici_adi}`;
      detailByArac.set(aracKey, [...(detailByArac.get(aracKey) || []), detail]);
      detailByAracAndCustomer.set(customerKey, [...(detailByAracAndCustomer.get(customerKey) || []), detail]);
    }

    return Object.entries(groups).map(([aracNo, groupRows]) => {
      const firstRow = groupRows[0];
      const assignmentStartDate = getCurrentDateInputValue();
      const notes = Array.from(new Set([...(groupRows.map((r) => r.not).filter(Boolean)), ...((detailByArac.get(aracNo) || []).map((r) => r.not).filter(Boolean))]));
      const assignmentStartTime = extractLoadingStartTimeFromNotes(notes);
      const stops = groupRows.map((r) => {
        const detailKey = `${aracNo}__${r.alici_kodu || r.alici_adi}`;
        const relatedDetails = detailByAracAndCustomer.get(detailKey) || [];
        const stopVolume = relatedDetails.reduce((sum, item) => sum + (item.m3toplam || 0), 0);
        const matchedCustomer = matchedCustomers[r.alici_kodu] || matchedCustomers[r.alici_adi] || null;
        return {
          customerCode: r.alici_kodu,
          customerName: r.alici_adi,
          city: r.sehir,
          district: r.ilce,
          volume: stopVolume,
          loadType: r.yukturu || '',
          tonnage: r.tonaj || 0,
          existingCustomerId: matchedCustomers[r.alici_kodu]?.id ? String(matchedCustomers[r.alici_kodu].id) : matchedCustomers[r.alici_adi]?.id ? String(matchedCustomers[r.alici_adi].id) : undefined,
          customerType: matchedCustomer?.customer_type === 'warehouse' ? 'warehouse' : 'customer',
          latitude: matchedCustomer?.latitude ? Number(matchedCustomer.latitude) : null,
          longitude: matchedCustomer?.longitude ? Number(matchedCustomer.longitude) : null,
          address: matchedCustomer?.address || '',
          channelType: matchedCustomer?.kanal_tipi || '',
          acceptanceStartTime: matchedCustomer?.unloading_start_time || null,
          acceptanceEndTime: matchedCustomer?.unloading_end_time || null,
          serviceDurationMinutes: matchedCustomer?.unloading_service_duration_minutes != null ? Number(matchedCustomer.unloading_service_duration_minutes) : 60,
          waitDurationMinutes: matchedCustomer?.unloading_wait_duration_minutes != null ? Number(matchedCustomer.unloading_wait_duration_minutes) : 0,
        };
      });
      const detailForArac = detailByArac.get(aracNo) || [];
      const totalVolume = detailForArac.reduce((sum, item) => sum + (item.m3toplam || 0), 0);
      const loadTypes = Array.from(new Set(groupRows.map((item) => item.yukturu).filter(Boolean)));
      const vehicleType = firstRow.arac_tipi || groupRows.find((r) => r.arac_tipi)?.arac_tipi || 'Tır Tenteli Kayar Perde';
      const existingCustomerId = matchedCustomers[firstRow.alici_kodu]?.customer_type === 'warehouse' || matchedCustomers[firstRow.alici_adi]?.customer_type === 'warehouse'
        ? undefined
        : matchedCustomers[firstRow.alici_kodu]?.id ? String(matchedCustomers[firstRow.alici_kodu].id) : matchedCustomers[firstRow.alici_adi]?.id ? String(matchedCustomers[firstRow.alici_adi].id) : undefined;
      const totalTonnage = Number(groupRows.find((row) => row.tonaj > 0)?.tonaj || 5);

      return {
        arac_no: aracNo,
        planCode: aracNo,
        assignmentStartDate,
        assignmentStartTime,
        pickupDate: combineAssignmentStart(assignmentStartDate, assignmentStartTime),
        pickupWarehouseId: whId,
        pickupWarehouseName: wh?.name || '-',
        pickupCity: wh?.city || '',
        pickupDistrict: wh?.district || '',
        pickupAddress: wh?.address || '',
        pickupLatitude: wh?.latitude ? Number(wh.latitude) : null,
        pickupLongitude: wh?.longitude ? Number(wh.longitude) : null,
        pickupServiceDurationMinutes: wh?.service_duration_minutes != null ? Number(wh.service_duration_minutes) : 60,
        deliveryCustomerCode: firstRow.alici_kodu,
        deliveryCustomerName: firstRow.alici_adi,
        deliveryCustomerCity: firstRow.sehir,
        deliveryCustomerDistrict: firstRow.ilce,
        deliveryChannelType: stops[0]?.channelType || '',
        deliveryLatitude: stops[0]?.latitude ?? null,
        deliveryLongitude: stops[0]?.longitude ?? null,
        deliveryAcceptanceStartTime: stops[0]?.acceptanceStartTime ?? null,
        deliveryAcceptanceEndTime: stops[0]?.acceptanceEndTime ?? null,
        deliveryServiceDurationMinutes: stops[0]?.serviceDurationMinutes ?? 60,
        deliveryWaitDurationMinutes: stops[0]?.waitDurationMinutes ?? 0,
        stopCount: groupRows.length,
        stops,
        totalVolume,
        totalTonnage,
        loadTypes,
        vehicleType,
        notes,
        existingCustomerId,
        excludeFromKds: false,
      };
    });
  };

  const openVehicleLocationInGoogleMaps = (latitude?: number | null, longitude?: number | null) => {
    if (latitude == null || longitude == null) {
      toast.error('Arac konumu bulunamadi');
      return;
    }
    const url = `https://www.google.com/maps?q=${encodeURIComponent(`${latitude},${longitude}`)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openDecisionRouteInGoogleMaps = (item: DecisionSupportPreview['selectedScenario']['decisions'][number]) => {
    const ownRoute = item.decision === 'OZMAL' ? item : item.nearestOwnAlternative;
    const vehicleLatitude = item.decision === 'OZMAL' ? item.vehicleLatitude : ownRoute?.vehicleLatitude;
    const vehicleLongitude = item.decision === 'OZMAL' ? item.vehicleLongitude : ownRoute?.vehicleLongitude;
    const pickupLatitude = item.pickupLatitude;
    const pickupLongitude = item.pickupLongitude;
    const returnHubLatitude = ownRoute?.returnHubLatitude ?? item.returnHubLatitude;
    const returnHubLongitude = ownRoute?.returnHubLongitude ?? item.returnHubLongitude;
    const stopSchedules = ownRoute?.stopSchedules || item.stopSchedules || [];

    if (vehicleLatitude == null || vehicleLongitude == null || pickupLatitude == null || pickupLongitude == null) {
      toast.error('Rota icin gerekli arac veya yukleme koordinati bulunamadi');
      return;
    }

    const waypointCoords = [
      `${pickupLatitude},${pickupLongitude}`,
      ...stopSchedules
        .filter((stop) => stop.latitude != null && stop.longitude != null)
        .map((stop) => `${stop.latitude},${stop.longitude}`),
    ];

    const destination = returnHubLatitude != null && returnHubLongitude != null
      ? `${returnHubLatitude},${returnHubLongitude}`
      : waypointCoords[waypointCoords.length - 1];
    if (!destination) {
      toast.error('Rota varis noktasi bulunamadi');
      return;
    }

    const intermediateWaypoints = returnHubLatitude != null && returnHubLongitude != null
      ? waypointCoords
      : waypointCoords.slice(0, -1);
    const url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(`${vehicleLatitude},${vehicleLongitude}`)}&destination=${encodeURIComponent(destination)}&waypoints=${encodeURIComponent(intermediateWaypoints.join('|'))}&travelmode=driving`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const exportDecisionPreviewToExcel = () => {
    const decisions = decisionPreview?.selectedScenario?.decisions || [];
    if (decisions.length === 0) {
      toast.error('Aktarilacak karar destek verisi yok');
      return;
    }
    const rows = decisions.map((item) => ({
      PLAN: item.planCode,
      BASLANGIC: [item.pickupCity, item.pickupDistrict].filter(Boolean).join(' / '),
      BITIS: [item.deliveryCity, item.deliveryDistrict].filter(Boolean).join(' / '),
      TESLIM_NOKTALARI: getDisplayedDeliveryStops(item).join(' | ') || item.deliveryName || '-',
      KANAL: item.deliveryChannelType || '-',
      UGRAMA: item.stopCount ?? 1,
      KARAR: item.decision === 'OZMAL' ? 'Ozmal' : 'Spot',
      ARAC: item.assignedVehiclePlate || '-',
      EN_YAKIN_OZMAL: item.nearestOwnAlternative?.assignedVehiclePlate || '-',
      EN_YAKIN_NEDEN_ATANMADI: item.nearestOwnExclusionReason || (item.nearestOwnAlternative ? 'Atandi (en yakin ozmal)' : '-'),
      SURUCU: item.driverName || '-',
      SON_KONUM_KAYNAGI: item.locationSource || item.nearestOwnAlternative?.locationSource || '-',
      IKAMET_IL: item.residenceCity || '-',
      KALAN_TAKO_SAAT: item.remainingTakoHours ?? item.nearestOwnAlternative?.remainingTakoHours ?? '-',
      HAFTALIK_KALAN_ONCE: item.weeklyRemainingBefore ?? getDisplayedWeeklyRemainingHours(item) ?? '-',
      HAFTALIK_KALAN_SONRA: item.weeklyRemainingAfter ?? (item.decision === 'OZMAL' ? (item.weeklyRemainingBefore ?? getDisplayedWeeklyRemainingHours(item) ?? '-') : '-'),
      IKI_HAFTALIK_KALAN_ONCE: item.twoWeekRemainingBefore ?? getDisplayedTwoWeekRemainingHours(item) ?? '-',
      IKI_HAFTALIK_KALAN_SONRA: item.twoWeekRemainingAfter ?? (item.decision === 'OZMAL' ? (item.twoWeekRemainingBefore ?? getDisplayedTwoWeekRemainingHours(item) ?? '-') : '-'),
      HAFTALIK_UYGUN: getDisplayedOwnAlternative(item)?.weeklyDriveCompatible == null ? '-' : (getDisplayedOwnAlternative(item)?.weeklyDriveCompatible ? 'Evet' : 'Hayir'),
      IKI_HAFTALIK_UYGUN: getDisplayedOwnAlternative(item)?.twoWeekDriveCompatible == null ? '-' : (getDisplayedOwnAlternative(item)?.twoWeekDriveCompatible ? 'Evet' : 'Hayir'),
      MUSTERI_KABUL: item.deliveryAcceptanceWindowLabel || item.nearestOwnAlternative?.deliveryAcceptanceWindowLabel || item.spotAlternative?.deliveryAcceptanceWindowLabel || '-',
      YUKLEME_SERVIS_DK: getDisplayedOwnAlternative(item)?.pickupServiceDurationMinutes || item.spotAlternative?.pickupServiceDurationMinutes || '-',
      SON_KONUM_YUKLEME_YOL_KM: Number(item.driverToPickupKm || getDisplayedOwnAlternative(item)?.driverToPickupKm || 0).toFixed(1),
      BOS_KM: Number(getDisplayedOwnAlternative(item)?.emptyKm || item.emptyKm || 0).toFixed(1),
      HUB: item.returnHubName || getDisplayedOwnAlternative(item)?.returnHubName || item.spotAlternative?.returnHubName || '-',
      HUB_KM: Number(item.returnHubKm || getDisplayedOwnAlternative(item)?.returnHubKm || item.spotAlternative?.returnHubKm || 0).toFixed(1),
      ROTA_KM: Number(getDisplayedOwnAlternative(item)?.routeKm || item.routeKm || 0).toFixed(1),
      GUN: Number(getDisplayedOwnAlternative(item)?.operationDayCount || item.operationDayCount || 0).toFixed(0),
      SURUS_SAAT: Number(getDisplayedOwnAlternative(item)?.totalDriveHours || 0).toFixed(2),
      MOLA_SAAT: Number(getDisplayedOwnAlternative(item)?.totalBreakHours || 0).toFixed(2),
      DINLENME_SAAT: Number(getDisplayedOwnAlternative(item)?.totalRestHours || 0).toFixed(2),
      SERVIS_SAAT: Number(getDisplayedOwnAlternative(item)?.totalServiceHours || 0).toFixed(2),
      BEKLEME_SAAT: Number(getDisplayedOwnAlternative(item)?.totalWaitingHours || 0).toFixed(2),
      TOPLAM_OPERASYON_SAAT: Number(getDisplayedOwnAlternative(item)?.operationHours || item.operationHours || 0).toFixed(2),
      TOPLAM_KM: Number(getDisplayedOwnAlternative(item)?.totalKm || item.totalKm || 0).toFixed(1),
      ATAMA_BASLANGIC_TARIHI: formatTurkeyDate(item.assignmentStartTime || item.estimatedPickupArrivalAt),
      ATAMA_BASLANGIC_SAATI: formatTurkeyTime(item.assignmentStartTime || item.estimatedPickupArrivalAt),
      OZMAL_MALIYET_TL: getOwnCostBreakdownTotal(getDisplayedOwnAlternative(item)),
      PERSONEL_MALIYETI: Math.round(Number(getDisplayedOwnAlternative(item)?.personnelCost || 0)),
      PERSONEL_MALIYET_MODU: getDisplayedOwnAlternative(item)?.personnelCostMode === 'hourly' ? 'Saatlik' : 'Gunluk',
      PERSONEL_SAATLIK_TUTAR: Math.round(Number(getDisplayedOwnAlternative(item)?.hourlyPersonnelCost || 0)),
      PERSONEL_FATURALANAN_SAAT: Number(getDisplayedOwnAlternative(item)?.chargedPersonnelHours || 0).toFixed(2),
      SPOT_MALIYET: Math.round(Number(item.spotAlternative?.selectedTotalCost || item.spotAlternativeCost || 0)),
      KAR_GUN_TL: Number(item.profitPerDay || 0).toFixed(2),
      YUKLEMEYE_VARIS: formatDateTimeValue(item.estimatedPickupArrivalAt),
      ROTAYI_BITIRIS: formatDateTimeValue(item.estimatedRouteFinishAt),
      HUBA_VARIS: formatDateTimeValue(item.estimatedNearestHubArrivalAt),
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    decisions.forEach((item, index) => {
      if (item.decision !== 'OZMAL' || !item.assignedVehiclePlate) return;
      const tone = getVehicleRowColor(item.assignedVehiclePlate);
      if (!tone) return;
      const excelRowIndex = index + 1;
      for (let columnIndex = range.s.c; columnIndex <= range.e.c; columnIndex += 1) {
        const cellAddress = XLSX.utils.encode_cell({ r: excelRowIndex, c: columnIndex });
        if (!worksheet[cellAddress]) continue;
        worksheet[cellAddress].s = {
          fill: {
            patternType: 'solid',
            fgColor: { rgb: toExcelColor(tone.bg) },
          },
          border: {
            left: { style: 'thin', color: { rgb: toExcelColor(tone.border) } },
          },
        };
      }
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Karar Destek');
    XLSX.writeFile(workbook, 'karar-destek-onizleme.xlsx');
  };

  const runDecisionPreview = async (shipments: ParsedShipment[], distanceLimitKm: number, replicationCount: number, searchMethod: 'random_search' | 'kds' | 'kds_plus_kds', distanceProvider: 'bms' | 'google_maps' | 'openrouteservice' | 'osm') => {
    const includedShipments = shipments.filter((item) => !item.excludeFromKds);
    if (!autoDecisionSupport || includedShipments.length === 0) {
      await cancelDecisionPreviewJob(decisionPreviewJobId);
      setDecisionPreview(null);
      setDecisionPreviewJobId(null);
      setDecisionPreviewJob(null);
      return;
    }
    setDecisionPreviewLoading(true);
    try {
      await cancelDecisionPreviewJob(decisionPreviewJobId);
      const response: any = await supplyService.previewDecisionSupportImport({ shipments: includedShipments, distanceLimitKm, replicationCount: searchMethod === 'random_search' ? replicationCount : 1, searchMethod, distanceProvider, ignoreVehicleStatusForMatchingDepot });
      const jobSnapshot = response?.data || null;
      setDecisionPreview(null);
      setDecisionPreviewJob(jobSnapshot);
      setDecisionPreviewJobId(jobSnapshot?.jobId || null);
    } catch (error: any) {
      console.error('Decision support preview error:', error);
      toast.error(error?.message || 'Karar destek önizlemesi alınamadı');
      setDecisionPreview(null);
      setDecisionPreviewJobId(null);
      setDecisionPreviewJob(null);
    } finally {
    }
  };

  useEffect(() => {
    if (step !== 'preview') return;
    if (!autoDecisionSupport || kdsIncludedShipments.length === 0) {
      void cancelDecisionPreviewJob(decisionPreviewJobId);
      setDecisionPreview(null);
      setDecisionPreviewLoading(false);
      setDecisionPreviewJob(null);
      setDecisionPreviewJobId(null);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void runDecisionPreview(parsedShipments, decisionDistanceLimitKm, decisionReplicationCount, decisionSearchMethod, decisionDistanceProvider);
    }, 150);

    return () => window.clearTimeout(timeoutId);
  }, [parsedShipments, decisionDistanceLimitKm, decisionReplicationCount, decisionSearchMethod, decisionDistanceProvider, ignoreVehicleStatusForMatchingDepot, autoDecisionSupport, step, kdsIncludedShipments.length]);

  useEffect(() => {
    if (!decisionPreviewJobId || step !== 'preview' || !autoDecisionSupport) return;
    let cancelled = false;
    let timeoutId: number | null = null;

    const poll = async () => {
      try {
        const response: any = await supplyService.getPreviewDecisionSupportImportStatus(decisionPreviewJobId);
        if (cancelled || !response?.success) return;
        const snapshot = response.data as DecisionPreviewJobSnapshot;
        setDecisionPreviewJob(snapshot);
        if (snapshot.status === 'completed') {
          setDecisionPreview(snapshot.data || null);
          setDecisionPreviewLoading(false);
          return;
        }
        if (snapshot.status === 'failed' || snapshot.status === 'cancelled') {
          setDecisionPreview(null);
          setDecisionPreviewLoading(false);
          if (snapshot.status === 'failed') {
            toast.error(snapshot.error || 'Karar destek önizlemesi alınamadı');
          }
          return;
        }
        setDecisionPreviewLoading(true);
        timeoutId = window.setTimeout(poll, 1500);
      } catch (error) {
        if (!cancelled) {
          const statusCode = (error as any)?.statusCode || (error as any)?.status;
          if (statusCode === 404) {
            setDecisionPreviewLoading(false);
            setDecisionPreviewJob(null);
            setDecisionPreviewJobId(null);
            return;
          }
          console.error('Decision support preview status poll error:', error);
          timeoutId = window.setTimeout(poll, 2500);
        }
      }
    };

    void poll();

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [decisionPreviewJobId, step, autoDecisionSupport]);

  const handleCreate = async () => {
    setCreating(true);
    setCreateErrors([]);
    const errors: string[] = [];
    const duplicatePlanCodeErrors: string[] = [];
    let created = 0;
    let updated = 0;

    const decisionsByPlanCode: Record<string, any> = {};
    if (decisionPreview?.selectedScenario?.decisions) {
      for (const d of decisionPreview.selectedScenario.decisions) {
        decisionsByPlanCode[d.planCode] = d;
      }
    }

    const existingCustomers: any[] = await customerService.getCustomers().then((res: any) => Array.isArray(res?.data) ? res.data : []).catch(() => []);
    const planCodes = new Set(parsedShipments.map((s) => s.planCode).filter(Boolean));
    const existingByPlanCode: Map<string, any> = new Map();
    const existingByShipmentId: Map<string, any> = new Map();
    if (planCodes.size > 0) {
      try {
        const allRecords: any[] = await supplyService.getAll().then((res: any) => Array.isArray(res?.data) ? res.data : []);
        for (const rec of allRecords) {
          if (rec?.id != null) {
            existingByShipmentId.set(String(rec.id), rec);
          }
          if (rec.planKodu && planCodes.has(String(rec.planKodu))) {
            existingByPlanCode.set(String(rec.planKodu), rec);
          }
        }
      } catch { /* ignore fetch error */ }
    }

    for (const shipment of parsedShipments) {
      try {
        const resolvedStops = [] as Array<{ customerId: string | number | null; customerCode: string; customerName: string; customerType?: 'customer' | 'warehouse'; city: string; district: string; latitude: number | null; longitude: number | null; volume: number; loadType: string; tonnage: number }>;

        for (const stop of shipment.stops) {
          let customerId = stop.existingCustomerId || null;
          const warehouseByCode = findExistingWarehouseByCode(warehouses, stop.customerCode);
          if (warehouseByCode) customerId = null;
          let customerRow = warehouseByCode
            ? mapWarehouseToCustomerLike(warehouseByCode, stop.customerCode, stop.customerName)
            : customerId
              ? existingCustomers.find((c) => String(c.id) === String(customerId))
              : findExistingCustomer(existingCustomers, stop.customerCode, stop.customerName);

          if (!customerRow) {
            const createRes = await customerService.createCustomer({
              companyCode: stop.customerCode,
              companyName: stop.customerName,
              city: stop.city,
              district: stop.district,
              customerType: 'Alıcı',
              paymentTerms: 0,
            });
            customerId = createRes?.data?.id || null;
            customerRow = {
              id: customerId,
              company_code: stop.customerCode,
              customer_code: stop.customerCode,
              code: stop.customerCode,
              company_name: stop.customerName,
              city: stop.city,
              district: stop.district,
              latitude: null,
              longitude: null,
            };
            existingCustomers.push(customerRow as any);
          } else {
            customerId = customerRow.customer_type === 'warehouse' ? null : customerRow.id;
          }

          resolvedStops.push({
            customerId,
            customerCode: stop.customerCode,
            customerName: stop.customerName,
            customerType: customerRow?.customer_type === 'warehouse' ? 'warehouse' : 'customer',
            city: stop.city,
            district: stop.district,
            latitude: customerRow?.latitude ? Number(customerRow.latitude) : null,
            longitude: customerRow?.longitude ? Number(customerRow.longitude) : null,
            volume: stop.volume,
            loadType: stop.loadType,
            tonnage: stop.tonnage,
          });
        }

        const loadingStops = resolvedStops.filter((stop) => stop.customerType === 'warehouse');
        const deliveryStops = resolvedStops.filter((stop) => stop.customerType !== 'warehouse');
        const primaryStop = deliveryStops[0] || resolvedStops[0];
        const deliveryCustomerId = primaryStop?.customerType === 'warehouse'
          ? null
          : primaryStop?.customerId != null && String(primaryStop.customerId).trim() !== '' ? primaryStop.customerId : null;
        const deliveryLatitude = primaryStop?.latitude ?? null;
        const deliveryLongitude = primaryStop?.longitude ?? null;

        const pickupLatitude = selectedWarehouse?.latitude ? Number(selectedWarehouse.latitude) : null;
        const pickupLongitude = selectedWarehouse?.longitude ? Number(selectedWarehouse.longitude) : null;
        const rawInvoiceCustomerId = selectedWarehouse?.invoice_customer_id != null ? String(selectedWarehouse.invoice_customer_id).trim() : '';
        const invoiceCustomerId = /^\d+$/.test(rawInvoiceCustomerId) ? rawInvoiceCustomerId : null;
        const invoiceCustomerName = selectedWarehouse?.invoice_customer_name || null;

        const intermediateStops = deliveryStops.slice(1).map((stop) => ({
          customer_id: stop.customerId,
          label: stop.customerName,
          address: '',
          city: stop.city,
          district: stop.district,
          latitude: stop.latitude,
          longitude: stop.longitude,
        }));
        const pickupIntermediateStops = loadingStops.map((stop) => ({
          customer_id: null,
          label: stop.customerName,
          address: '',
          city: stop.city,
          district: stop.district,
          latitude: stop.latitude,
          longitude: stop.longitude,
        }));

        const pickupStopSummary = pickupIntermediateStops.map((stop) => stop.label).filter(Boolean).join(', ');
        const deliveryStopSummary = intermediateStops.map((stop) => stop.label).filter(Boolean).join(', ');
        const noteParts = shipment.notes.filter(Boolean);
        if (pickupStopSummary) noteParts.push(`Yükleme Ara Noktaları: ${pickupStopSummary}`);
        if (deliveryStopSummary) noteParts.push(`Boşaltma Ara Noktaları: ${deliveryStopSummary}`);

        const decision = decisionsByPlanCode[shipment.planCode];
        const isApproved = decision && assignmentApproval[shipment.planCode] !== false;
        const assignedVehicleId = isApproved && decision?.decision === 'OZMAL' && decision?.assignedVehicleId ? Number(decision.assignedVehicleId) : null;
        const assignmentStartAt = combineAssignmentStart(shipment.assignmentStartDate, shipment.assignmentStartTime) || shipment.pickupDate || new Date().toISOString().slice(0, 16);

        const payload = {
          durum: assignedVehicleId ? 'tedarik-bekleniyor' : 'tedarik-bekleniyor',
          aracTipi: assignedVehicleId ? 'Filo' : '',
          assignedVehicleId: assignedVehicleId || undefined,
          aracTuru: shipment.vehicleType,
          yukTipi: shipment.loadTypes.join(', ') || primaryStop?.loadType || '',
          yukAgirlik: shipment.totalTonnage || 0,
          yukHacim: shipment.totalVolume,
          palletCount: 0,
          vehicleCount: 1,
          stopCount: shipment.stopCount,
          yuklemeTalepTarihi: assignmentStartAt,
          siparisTarihi: assignmentStartAt,
          faturaBasligi: invoiceCustomerName || shipment.deliveryCustomerName,
          mesafe: 0,
          maliyet: 0,
          targetPrice: 0,
          navlun: 0,
          yuklemeMusterisiAd: shipment.pickupWarehouseName,
          yuklemeIl: selectedWarehouse?.city || '',
          yuklemeIlce: selectedWarehouse?.district || '',
          yuklemeAdres: selectedWarehouse?.address || '',
          teslimMusterisiAd: primaryStop?.customerName || shipment.deliveryCustomerName,
          teslimIl: primaryStop?.city || shipment.deliveryCustomerCity,
          teslimIlce: primaryStop?.district || shipment.deliveryCustomerDistrict,
          teslimAdres: '',
          pickupLatitude,
          pickupLongitude,
          deliveryLatitude,
          deliveryLongitude,
          invoiceCustomerId,
          nearestHubName: decision?.returnHubName || decision?.nearestOwnAlternative?.returnHubName || decision?.spotAlternative?.returnHubName || null,
          nearestHubKm: decision?.returnHubKm || decision?.nearestOwnAlternative?.returnHubKm || decision?.spotAlternative?.returnHubKm || null,
          nearestHubLatitude: decision?.returnHubLatitude || decision?.nearestOwnAlternative?.returnHubLatitude || decision?.spotAlternative?.returnHubLatitude || null,
          nearestHubLongitude: decision?.returnHubLongitude || decision?.nearestOwnAlternative?.returnHubLongitude || decision?.spotAlternative?.returnHubLongitude || null,
          pickupCustomerId: null,
          deliveryCustomerId,
          pickupWarehouseId: shipment.pickupWarehouseId,
          planKodu: shipment.planCode || null,
          siparisKodu: `${shipment.arac_no}-${Date.now().toString().slice(-5)}`,
          notlar: noteParts.join(' | ') || null,
          intermediateStops: {
            pickup: pickupIntermediateStops,
            delivery: intermediateStops,
          },
        };

        const existing = shipment.sourceShipmentId
          ? existingByShipmentId.get(String(shipment.sourceShipmentId)) || (shipment.planCode ? existingByPlanCode.get(shipment.planCode) : null)
          : (shipment.planCode ? existingByPlanCode.get(shipment.planCode) : null);
        if (existing) {
          await supplyService.update(existing.id, payload);
          updated++;
        } else {
          await supplyService.create(payload);
          created++;
        }
      } catch (error: any) {
        const errorMessage = `${shipment.arac_no} -> ${shipment.deliveryCustomerName}: ${error?.message || 'Bilinmeyen hata'}`;
        errors.push(errorMessage);
        if ((error?.status === 409 || error?.statusCode === 409) && String(error?.message || '').toLocaleLowerCase('tr-TR').includes('plan kodu')) {
          duplicatePlanCodeErrors.push(errorMessage);
        }
      }
    }

    setCreateErrors(errors);
    setCreating(false);

    if (created > 0 || updated > 0) {
      const parts: string[] = [];
      if (created > 0) parts.push(`${created} oluşturuldu`);
      if (updated > 0) parts.push(`${updated} güncellendi`);
      toast.success(`Sevk siparişi: ${parts.join(', ')}`);
        if (errors.length === 0) {
          setTimeout(() => {
            onCreated?.();
            handleModalClose();
          }, 1000);
        }
    } else {
      toast.error('Hiçbir sipariş oluşturulamadı');
    }

    if (duplicatePlanCodeErrors.length > 0) {
      toast.error(`${duplicatePlanCodeErrors.length} satır plan kodu çakışması nedeniyle işlenemedi`);
      showAlert({
        tone: 'warning',
        title: 'Plan Kodu Çakışmaları',
        message: `Aşağıdaki satırlar aynı plan kodu ile mevcut kayıt bulunduğu için açılmadı:\n\n${duplicatePlanCodeErrors.join('\n')}`,
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-3 sm:p-6 backdrop-blur-sm">
      <div className="h-[96vh] w-full max-w-[96vw] overflow-auto rounded-3xl bg-white shadow-2xl xl:max-w-[1800px]" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex flex-col gap-3 border-b border-slate-300 bg-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5">
          <div className="min-w-0">
            <h2 className="text-2xl font-semibold text-slate-900">Excel'den Toplu Sipariş</h2>
            <p className="mt-1 text-sm text-slate-700">Araç planı Excel dosyasından sevk siparişleri oluştur</p>
          </div>
          <button type="button" onClick={handleModalClose} className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-4 py-4 sm:px-6 sm:py-6">
          {step === 'upload' && (
            <div className="space-y-6">
              <div className="rounded-2xl border-2 border-dashed border-violet-300 bg-violet-50 p-12 text-center">
                <FileSpreadsheet className="w-16 h-16 text-violet-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Excel Dosyası Yükle</h3>
                <p className="text-sm text-slate-500 mb-4">Araç planı Excel dosyasını seçin (.xlsx, .xls)</p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <button type="button" onClick={downloadTemplate} className="inline-flex items-center gap-2 rounded-xl border border-violet-200 bg-white px-6 py-3 text-violet-700 font-semibold hover:bg-violet-100">
                    <FileSpreadsheet className="w-4 h-4" />
                    Örnek Sipariş İndir
                  </button>
                  <label className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-6 py-3 text-white font-semibold cursor-pointer hover:bg-violet-700">
                    <Upload className="w-4 h-4" />
                    Dosya Seç
                    <input type="file" accept=".xlsx,.xls" onChange={handleFileUpload} className="hidden" />
                  </label>
                </div>
              </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Beklenen Kolonlar:</h4>
                  <div className="grid grid-cols-1 gap-2 text-xs text-slate-600 sm:grid-cols-2 md:grid-cols-4">
                    <div className="bg-white rounded-lg px-3 py-2"><span className="font-semibold">ARAC_NO</span> - Araç kodu</div>
                   <div className="bg-white rounded-lg px-3 py-2"><span className="font-semibold">ALICI</span> - Müşteri kodu</div>
                   <div className="bg-white rounded-lg px-3 py-2"><span className="font-semibold">ALICI_ADI</span> - Müşteri adı</div>
                   <div className="bg-white rounded-lg px-3 py-2"><span className="font-semibold">SEHIR / İLÇE</span> - Teslim noktası</div>
                   <div className="bg-white rounded-lg px-3 py-2"><span className="font-semibold">BRÜT KG</span> - Tonaj (brüt ağırlık)</div>
                   <div className="bg-white rounded-lg px-3 py-2"><span className="font-semibold">TOPLAM_VOLUME</span> - Sevk Plan sekmesi</div>
                   <div className="bg-white rounded-lg px-3 py-2"><span className="font-semibold">Yükleme Tipi</span> - Paletli / Paletsiz</div>
                   <div className="bg-white rounded-lg px-3 py-2"><span className="font-semibold">not</span> - Sipariş notu</div>
                 </div>
                 <p className="text-xs text-slate-400 mt-2">Eski şablon (ARAÇ PLANI / TONAJ / ALICI_KODU) ve güncel plan (Sayfa1 / BRÜT KG / ALICI) otomatik algılanır.</p>
               </div>

               <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeOpenUnassignedInPlanTable}
                    onChange={(e) => setIncludeOpenUnassignedInPlanTable(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                  />
                  <span>
                    <span className="block text-sm font-semibold text-slate-800">Atanmamış açık siparişleri plan tablosuna dahil et</span>
                    <span className="mt-1 block text-xs text-slate-500">İşaretli değilse açık ve henüz atanmamış sevkler önizleme plan tablosuna eklenmez; bu yüzden karar destek önizlemesine de gelmez.</span>
                  </span>
                </label>

                <button type="button" onClick={handleLoadOnlyOpenUnassigned} className="w-full rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50 p-8 text-center hover:bg-emerald-100 transition-colors">
                  <Truck className="mx-auto mb-2 h-10 w-10 text-emerald-400" />
                  <div className="text-sm font-semibold text-emerald-800">Excel Yüklemeden Karar Destek Çalıştır</div>
                  <div className="mt-1 text-xs text-emerald-600">Mevcut atanmamış açık siparişleri kullanarak doğrudan karar destek önizlemesine geç</div>
                </button>
             </div>
           )}

          {step === 'warehouse' && (
            <div className="space-y-6">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span className={`w-8 h-8 rounded-full flex items-center justify-center ${step !== 'upload' ? 'bg-emerald-500 text-white' : 'bg-slate-200'}`}>1</span>
                <span>Dosya yüklendi</span>
                <span className="mx-2">→</span>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${step === 'warehouse' ? 'bg-violet-600 text-white' : 'bg-slate-200'}`}>2</span>
                <span className="font-semibold">Yükleme noktası seç</span>
              </div>

              <div className="rounded-xl border border-slate-200 p-4 bg-slate-50">
                <div className="text-sm text-slate-600 mb-1">{excelRows.length} satır okundu</div>
                <div className="text-xs text-slate-400">
                  {new Set(excelRows.map((r) => r.arac_no).filter(Boolean)).size} araç, {new Set(excelRows.map((r) => r.alici_kodu).filter(Boolean)).size} farklı alıcı
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Yükleme Noktası (Depo)</label>
                <select
                  value={selectedWarehouseId}
                  onChange={(e) => setSelectedWarehouseId(e.target.value)}
                  className="w-full h-12 rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-800 outline-none focus:border-violet-500"
                >
                  <option value="">Depo seçiniz...</option>
                  {warehouses.map((wh) => (
                    <option key={wh.id} value={wh.id}>{wh.name}{wh.city ? ` - ${wh.city}` : ''}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Mesafe Hesaplama Servisi</label>
                <select
                  value={decisionDistanceProvider}
                  onChange={(e) => setDecisionDistanceProvider((e.target.value === 'google_maps' || e.target.value === 'osm' || e.target.value === 'openrouteservice') ? e.target.value : 'bms')}
                  className="w-full h-12 rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-800 outline-none focus:border-violet-500"
                >
                  <option value="osm">OpenStreetMap</option>
                  <option value="bms">BMS</option>
                  <option value="google_maps">Google API</option>
                  <option value="openrouteservice">OpenRouteService</option>
                </select>
              </div>

              <div className="flex justify-end gap-3">
                <button onClick={() => setStep('upload')} className="rounded-xl border border-slate-200 px-5 py-3 text-sm font-medium text-slate-600 hover:bg-slate-50">Geri</button>
                <button onClick={handleWarehouseSelect} disabled={!selectedWarehouseId} className="rounded-xl bg-violet-600 px-5 py-3 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
                  Devam Et
                </button>
              </div>
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-6">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span className="w-8 h-8 rounded-full flex items-center justify-center bg-emerald-500 text-white">1</span>
                <span>Dosya yüklendi</span>
                <span className="mx-2">→</span>
                <span className="w-8 h-8 rounded-full flex items-center justify-center bg-emerald-500 text-white">2</span>
                <span>Depo seçildi</span>
                <span className="mx-2">→</span>
                <span className="w-8 h-8 rounded-full flex items-center justify-center font-semibold bg-violet-600 text-white">3</span>
                <span className="font-semibold">Önizleme ve onay</span>
              </div>

              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <div className="font-semibold mb-1">Müşteri Durumu</div>
                    {Object.entries(customerStatuses).map(([code, status]) => (
                      <div key={code} className="flex items-center gap-2">
                        {status === 'ok' || status === 'warehouse' ? (
                          <CheckCircle2 className={`w-4 h-4 ${status === 'warehouse' ? 'text-blue-600' : 'text-emerald-600'}`} />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-amber-600" />
                        )}
                        <span>{code}</span>
                        <span className="text-xs text-amber-600">
                          {status === 'warehouse' ? 'Depo' : status === 'ok' ? 'Kayıtlı' : 'Yeni oluşturulacak'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="space-y-4">
                  <div className="grid gap-4 xl:grid-cols-[minmax(260px,1.2fr)_200px_200px_180px] xl:items-end">
                    <label className="flex min-h-[76px] items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                      <input
                        type="checkbox"
                        checked={autoDecisionSupport}
                        onChange={(e) => setAutoDecisionSupport(e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-slate-300"
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold leading-5 text-slate-900">Karar destek önizlemesi</div>
                        <div className="mt-1 text-xs leading-5 text-slate-500">İçeri aktarmadan önce özmal ve spot önerilerini hesapla</div>
                      </div>
                    </label>
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600">Mesafe Kısıtı</label>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={decisionDistanceLimitKm}
                        onChange={(e) => setDecisionDistanceLimitKm(Math.max(1, Number(e.target.value || 1)))}
                        disabled={!autoDecisionSupport}
                        className="h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-800 outline-none focus:border-violet-500 disabled:opacity-60"
                      />
                      <div className="mt-2 flex flex-wrap gap-2">
                        {[5, 10, 20, 30, 40, 50, 100, 150, 200].map((km) => (
                          <button
                            key={km}
                            type="button"
                            onClick={() => setDecisionDistanceLimitKm(km)}
                            disabled={!autoDecisionSupport}
                            className={`rounded-lg border px-2.5 py-1 text-xs font-medium ${decisionDistanceLimitKm === km ? 'border-violet-400 bg-violet-100 text-violet-700' : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100'} disabled:opacity-60`}
                          >
                            {km} km
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600">Yöntem</label>
                      <select
                        value={decisionSearchMethod}
                        onChange={(e) => setDecisionSearchMethod((e.target.value === 'kds' || e.target.value === 'kds_plus_kds') ? e.target.value : 'random_search')}
                        disabled={!autoDecisionSupport}
                        className="h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-800 outline-none focus:border-violet-500 disabled:opacity-60"
                        >
                          <option value="random_search">Rastgele Arama</option>
                          <option value="kds">KDS</option>
                          <option value="kds_plus_kds">KDS+KDS</option>
                        </select>
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600">Replikasyon</label>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={decisionSearchMethod === 'random_search' ? decisionReplicationCount : 1}
                        onChange={(e) => setDecisionReplicationCount(Math.max(1, Number(e.target.value || 20)))}
                        disabled={!autoDecisionSupport || decisionSearchMethod !== 'random_search'}
                        className="h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-800 outline-none focus:border-violet-500 disabled:opacity-60"
                      />
                    </div>
                  </div>
                  <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                    <input
                      type="checkbox"
                      checked={ignoreVehicleStatusForMatchingDepot}
                      onChange={(e) => setIgnoreVehicleStatusForMatchingDepot(e.target.checked)}
                      disabled={!autoDecisionSupport}
                      className="mt-1 h-4 w-4 rounded border-slate-300"
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold leading-5 text-slate-900">Varış noktası seçilen depo olan sevkiyatlarda boş/dolu ve aktif sevkiyatı olması durumunu dikkate alma</div>
                      <div className="mt-1 text-xs leading-5 text-slate-500">İşaretliyse devam eden son sevkiyatının varış deposu seçilen yükleme noktası olan araçlar, sürücünün boş/dolu işareti ve aktif sevkiyatı olması dikkate alınmadan alternatif özmal listesine alınabilir. Mesafe kısıtı yine uygulanır.</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                    <input
                      type="checkbox"
                      checked={alignAssignmentStartBackwardFromDeliveryWindow}
                      onChange={(e) => setAlignAssignmentStartBackwardFromDeliveryWindow(e.target.checked)}
                      disabled={!parsedShipments.some((shipment) => Boolean(getBackwardPlannedAssignmentStart(shipment)))}
                      className="mt-1 h-4 w-4 rounded border-slate-300"
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold leading-5 text-slate-900">Başlama sürelerini ilk boşaltma saatinden geriye doğru ayarla</div>
                      <div className="mt-1 text-xs leading-5 text-slate-500">
                        {parsedShipments.some((shipment) => Boolean(getBackwardPlannedAssignmentStart(shipment)))
                          ? 'İlk teslim noktasının boşaltma başlangıç saatinden; yükleme servis süresi ve tahmini sürüş düşülerek otomatik başlangıç oluşturulur'
                          : 'İlk teslim noktasında boşaltma başlangıç saati tanımlı plan bulunmuyor'}
                      </div>
                      {alignAssignmentStartBackwardFromDeliveryWindow ? (
                        <div className="mt-2 text-[11px] leading-5 text-violet-700">
                          {parsedShipments.find((shipment) => Boolean(getBackwardPlannedAssignmentExplanation(shipment)))
                            ? getBackwardPlannedAssignmentExplanation(parsedShipments.find((shipment) => Boolean(getBackwardPlannedAssignmentExplanation(shipment)))!)
                            : null}
                        </div>
                      ) : null}
                    </div>
                  </label>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => void runDecisionPreview(parsedShipments, decisionDistanceLimitKm, decisionReplicationCount, decisionSearchMethod, decisionDistanceProvider)}
                      disabled={!autoDecisionSupport || decisionPreviewLoading || kdsIncludedShipments.length === 0 || decisionEstimateLoading}
                      className="inline-flex h-11 items-center justify-center rounded-xl bg-violet-600 px-6 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
                    >
                      {decisionPreviewLoading ? 'Hesaplaniyor...' : 'Karar Destek Hesapla'}
                    </button>
                  </div>
                </div>
                <div className="mt-4 overflow-x-auto">
                  <div className="inline-flex min-w-full gap-2 rounded-2xl bg-white p-2 shadow-sm sm:min-w-0">
                    {[
                      { key: 'candidates', label: 'Alternatif Özmal Listesi' },
                      { key: 'plans', label: 'Plan Tablosu' },
                      { key: 'results', label: 'Karar Destek Sonuçları' },
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setPreviewTab(tab.key as 'candidates' | 'plans' | 'results')}
                        className={`flex-1 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-semibold transition ${previewTab === tab.key ? 'bg-violet-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
                {autoDecisionSupport ? (
                  previewTab === 'candidates' ? (
                  <div className="mt-4 rounded-xl border border-violet-200 bg-white p-4">
                    <div className="flex flex-col gap-4">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900">Tahmini BMS Tuketimi</div>
                        <div className="text-xs text-slate-500">Karar destek hesaplamasi butona basildiginda baslar. Asagidaki rakamlar baslamadan once tahmin olarak gosterilir.</div>
                        {decisionEstimateLoading ? (
                          <div className="mt-3 text-sm text-slate-600">Tahmin hesaplaniyor...</div>
                        ) : decisionEstimate ? (
                          <div className="mt-3 grid gap-3 md:grid-cols-4 text-xs text-slate-600">
                            <div className="rounded-lg bg-slate-50 px-3 py-2">Aktif Ozmal Arac: <span className="font-semibold text-slate-900">{decisionEstimate.activeOwnVehicleCount}</span></div>
                            <div className="rounded-lg bg-slate-50 px-3 py-2">Aktif Hub: <span className="font-semibold text-slate-900">{decisionEstimate.activeHubCount}</span></div>
                            <div className="rounded-lg bg-slate-50 px-3 py-2">Benzersiz Yukleme: <span className="font-semibold text-slate-900">{decisionEstimate.uniquePickupCount}</span></div>
                            <div className="rounded-lg bg-slate-50 px-3 py-2">Plan Sayisi: <span className="font-semibold text-slate-900">{decisionEstimate.shipmentCount}</span></div>
                          </div>
                        ) : (
                          <div className="mt-3 text-sm text-slate-500">Tahmin verisi alinamadi.</div>
                        )}
                        {decisionEstimate ? (
                          <div className="mt-3 rounded-lg bg-violet-50 px-3 py-3 text-xs text-violet-900">
                            <div>1. Son Konum → Yukleme: <span className="font-semibold">{decisionEstimate.activeOwnVehicleCount} x {decisionEstimate.uniquePickupCount} = {decisionEstimate.vehiclePickupPairs}</span></div>
                            <div>2. Plan ici rota ciftleri: <span className="font-semibold">{decisionEstimate.routeMatrixElements}</span></div>
                            <div>3. Bitis → Hub: <span className="font-semibold">{decisionEstimate.shipmentCount} x {decisionEstimate.activeHubCount} = {decisionEstimate.deliveryHubPairs}</span></div>
                            <div className="mt-1 border-t border-violet-200 pt-1">Toplam tahmini BMS tuketimi: <span className="font-bold">{decisionEstimate.totalEstimatedBmsPairs.toLocaleString('tr-TR')}</span></div>
                          </div>
                        ) : null}
                        {candidateVehicleRows.length ? (
                          <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200 bg-slate-50">
                            <table className="w-full min-w-[1180px] text-xs">
                              <thead className="bg-slate-100 text-slate-700">
                                <tr>
                                  <th className="px-3 py-2 text-left font-semibold">Çekici</th>
                                  <th className="px-3 py-2 text-left font-semibold">Sürücü</th>
                                  <th className="px-3 py-2 text-left font-semibold">Yüklemeye Uzaklık</th>
                                  <th className="px-3 py-2 text-left font-semibold">En Yakın Yükleme</th>
                                  <th className="px-3 py-2 text-left font-semibold">Atamaya Uygun</th>
                                  <th className="px-3 py-2 text-left font-semibold">Aktiflik</th>
                                  <th className="px-3 py-2 text-left font-semibold">Devam Eden Görev</th>
                                  <th className="px-3 py-2 text-left font-semibold">Konum</th>
                                </tr>
                              </thead>
                              <tbody>
                                {candidateVehicleRows.map((vehicle) => (
                                  <tr key={String(vehicle.vehicleId)} className="border-t border-slate-200 text-slate-700">
                                    <td className="px-3 py-2 font-medium text-slate-900">{vehicle.assignedVehiclePlate || '-'}</td>
                                    <td className="px-3 py-2">{vehicle.driverName || '-'}</td>
                                    <td className="px-3 py-2">
                                      {vehicle.distanceToPickupKm != null ? `${Number(vehicle.distanceToPickupKm).toFixed(1)} km` : '-'}
                                    </td>
                                    <td className="px-3 py-2">{[vehicle.nearestPickupPlanCode, vehicle.nearestPickupName].filter(Boolean).join(' • ') || '-'}</td>
                                    <td className="px-3 py-2">
                                      <span className={`inline-flex rounded-full px-2 py-1 font-semibold ${vehicle.isAssignable ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                        {vehicle.isAssignable ? 'Uygun' : 'Uygun Değil'}
                                      </span>
                                      {!vehicle.isAssignable && vehicle.exclusionReason ? (
                                        <div className="mt-1 max-w-[220px] text-[11px] leading-4 text-rose-700">{vehicle.exclusionReason}</div>
                                      ) : null}
                                    </td>
                                    <td className="px-3 py-2">
                                      <div className="flex flex-wrap gap-1">
                                        <span className={`inline-flex rounded-full px-2 py-1 font-semibold ${vehicle.isVehicleActive ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>Araç</span>
                                        <span className={`inline-flex rounded-full px-2 py-1 font-semibold ${vehicle.isDriverActive ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>Sürücü</span>
                                      </div>
                                    </td>
                                    <td className="px-3 py-2">
                                      {vehicle.hasActiveTask ? (
                                        <div className="space-y-1">
                                          <div className="font-medium text-amber-700">{vehicle.activeTaskCount} aktif görev</div>
                                          <div className="max-w-[220px] truncate text-slate-500" title={(vehicle.activeTaskCodes || []).join(', ')}>{(vehicle.activeTaskCodes || []).join(', ')}</div>
                                        </div>
                                      ) : 'Yok'}
                                    </td>
                                    <td className="max-w-[260px] truncate px-3 py-2" title={vehicle.currentAddress || ''}>{vehicle.currentAddress || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : decisionEstimate?.candidateVehicles?.length ? (
                          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                            Seçili km sınırı ve aktif görev durumuna göre gösterilecek uygun özmal bulunamadı.
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  ) : null
                ) : null}
              </div>

              {autoDecisionSupport && previewTab === 'results' && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">Karar Destek Önizlemesi</div>
                        <div className="text-xs text-slate-600">Mesafe servisi: {decisionPreview?.distanceProvider === 'google_maps' ? 'Google API' : decisionPreview?.distanceProvider === 'openrouteservice' ? 'OpenRouteService' : decisionPreview?.distanceProvider === 'osm' ? 'OpenStreetMap' : 'BMS'}, mesafe kısıtı: {decisionDistanceLimitKm} km, yöntem: {decisionPreview?.searchMethod === 'kds' ? 'KDS' : decisionPreview?.searchMethod === 'kds_plus_kds' ? 'KDS+KDS' : 'Rastgele Arama'}, replikasyon: {decisionPreview?.replicationCount || (decisionSearchMethod === 'random_search' ? decisionReplicationCount : 1)}{decisionPreview?.durationMs ? `, süre: ${formatDurationLabel(decisionPreview.durationMs)}` : ''}</div>
                      </div>
                    <div className="flex items-center gap-3">
                      {decisionTimelineRows.length ? (
                        <button
                          type="button"
                          onClick={() => setShowDecisionTimeline(true)}
                          className="inline-flex items-center gap-2 rounded-xl border border-violet-300 bg-white px-3 py-2 text-xs font-semibold text-violet-700 hover:bg-violet-50"
                        >
                          Zaman Cizelgesi
                        </button>
                      ) : null}
                      {decisionPreview?.selectedScenario?.decisions?.length ? (
                        <button
                          type="button"
                          onClick={exportDecisionPreviewToExcel}
                          className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          <FileSpreadsheet className="h-4 w-4" />
                          Karar destek onizlemesini excele aktar
                        </button>
                      ) : null}
                      {decisionPreviewLoading ? <Loader2 className="h-5 w-5 animate-spin text-blue-600" /> : null}
                    </div>
                  </div>

                  {decisionPreviewJob && (decisionPreviewJob.status === 'queued' || decisionPreviewJob.status === 'running') ? (
                    <div className="mb-4 rounded-xl border border-sky-200 bg-white p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-slate-900">Hesaplama sürüyor</div>
                          <div className="text-xs text-slate-600">{decisionPreviewJob.progress?.message || 'Karar destek önizlemesi hazırlanıyor...'}</div>
                        </div>
                        <div className="text-sm font-bold text-sky-700">%{Math.max(0, Math.min(100, Math.round(Number(decisionPreviewJob.progress?.percent || 0))))}</div>
                      </div>
                      <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full bg-sky-500 transition-all" style={{ width: `${Math.max(0, Math.min(100, Math.round(Number(decisionPreviewJob.progress?.percent || 0))))}%` }} />
                      </div>
                      {(decisionPreviewJob.progress?.stageCurrent || decisionPreviewJob.progress?.stageTotal) ? (
                        <div className="mt-3 rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-900">
                          İlerleme: <span className="font-semibold">{decisionPreviewJob.progress?.stageCurrent || 0}/{decisionPreviewJob.progress?.stageTotal || 0}</span>
                          {(decisionPreviewJob.progress?.stageSecondaryTotal || 0) > 0 ? (
                            <span> | Alt grup: <span className="font-semibold">{decisionPreviewJob.progress?.stageSecondaryCurrent || 0}/{decisionPreviewJob.progress?.stageSecondaryTotal || 0}</span></span>
                          ) : null}
                        </div>
                      ) : null}
                      <div className="mt-3 grid gap-3 md:grid-cols-6 text-xs text-slate-600">
                        <div className="rounded-lg bg-slate-50 px-3 py-2">Durum: <span className="font-semibold text-slate-900">{decisionPreviewJob.status === 'running' ? 'Çalışıyor' : 'Kuyrukta'}</span></div>
                        <div className="rounded-lg bg-slate-50 px-3 py-2">Süre: <span className="font-semibold text-slate-900">{formatDurationLabel(decisionPreviewJob.durationMs)}</span></div>
                        <div className="rounded-lg bg-slate-50 px-3 py-2">BMS Pair: <span className="font-semibold text-slate-900">{decisionPreviewJob.progress?.matrixElementCount || 0}</span></div>
                        <div className="rounded-lg bg-slate-50 px-3 py-2">Rota: <span className="font-semibold text-slate-900">{decisionPreviewJob.progress?.routeCompleted || 0}/{decisionPreviewJob.progress?.routeTotal || decisionPreviewJob.progress?.shipmentCount || 0}</span></div>
                        <div className="rounded-lg bg-slate-50 px-3 py-2">Alternatif: <span className="font-semibold text-slate-900">{decisionPreviewJob.progress?.shipmentCompleted || 0}/{decisionPreviewJob.progress?.shipmentTotal || decisionPreviewJob.progress?.shipmentCount || 0}</span></div>
                        <div className="rounded-lg bg-slate-50 px-3 py-2">Senaryo: <span className="font-semibold text-slate-900">{decisionPreviewJob.progress?.scenarioCompleted || 0}/{decisionPreviewJob.progress?.scenarioTotal || decisionPreviewJob.progress?.replicationCount || decisionReplicationCount}</span></div>
                      </div>
                    </div>
                  ) : null}

                  {decisionPreview?.selectedScenario ? (
                    <>
                      <div className="mb-4 grid gap-3 md:grid-cols-7">
                        <div className="rounded-xl bg-white px-4 py-3"><div className="text-[11px] text-slate-500">Özmal Atama</div><div className="mt-1 text-lg font-bold text-emerald-700">{decisionPreview.selectedScenario.summary.ozmalAssigned}</div></div>
                        <div className="rounded-xl bg-white px-4 py-3"><div className="text-[11px] text-slate-500">Spot Öneri</div><div className="mt-1 text-lg font-bold text-amber-700">{decisionPreview.selectedScenario.summary.spotAssigned}</div></div>
                        <div className="rounded-xl bg-white px-4 py-3"><div className="text-[11px] text-slate-500">Toplam Seçilen</div><div className="mt-1 text-lg font-bold text-slate-900">₺{Math.round(decisionPreview.selectedScenario.summary.totalSelectedCost).toLocaleString('tr-TR')}</div></div>
                        <div className="rounded-xl bg-white px-4 py-3"><div className="text-[11px] text-slate-500">Spot Referans</div><div className="mt-1 text-lg font-bold text-slate-900">₺{Math.round(decisionPreview.selectedScenario.summary.totalSpotReferenceCost).toLocaleString('tr-TR')}</div></div>
                        <div className="rounded-xl bg-white px-4 py-3"><div className="text-[11px] text-slate-500">En İyi Senaryo</div><div className="mt-1 text-lg font-bold text-emerald-700">#{decisionPreview.selectedScenario.scenarioNo}</div></div>
                        <div className="rounded-xl bg-white px-4 py-3"><div className="text-[11px] text-slate-500">En İyi / En Kötü Fark</div><div className="mt-1 text-lg font-bold text-rose-700">₺{Math.round(decisionPreview.bestWorstCostGap || 0).toLocaleString('tr-TR')}</div></div>
                        <div className="rounded-xl bg-white px-4 py-3"><div className="text-[11px] text-slate-500">Hesap Süresi</div><div className="mt-1 text-lg font-bold text-sky-700">{formatDurationLabel(decisionPreview.durationMs)}</div></div>
                      </div>

                      <div className="mb-4 rounded-xl border border-slate-200 bg-white px-4 py-3">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Neden rozetleri</div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {DECISION_REASON_BADGE_LEGEND.map((item) => (
                            <span key={item.label} className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-medium ${item.className}`}>
                              {item.label}
                            </span>
                          ))}
                        </div>
                      </div>

                      {decisionPreview.bmsLogs && decisionPreview.bmsLogs.length > 0 && (
                        <details className="mb-4 rounded-xl border border-amber-200 bg-amber-50 overflow-hidden">
                          <summary className="cursor-pointer px-4 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100">
                            BMS Rota Logları ({decisionPreview.bmsLogs.length} kayıt)
                          </summary>
                          <div className="max-h-48 overflow-y-auto p-3 space-y-1 text-xs">
                            {decisionPreview.bmsLogs.map((log, li) => (
                              <div key={li} className={`rounded px-2 py-1 font-mono ${log.ok ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                                <span className="font-semibold">#{log.attempt + 1}</span> — {log.ok ? `✓ ${log.km} km` : `✗ HTTP ${log.status}: ${log.message}`}
                              </div>
                            ))}
                          </div>
                        </details>
                      )}

                      <div className="mb-4 overflow-x-auto rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-violet-300" tabIndex={0} onKeyDown={handleHorizontalScrollKeyDown} style={SCROLL_WRAPPER_STYLE}>
                        <table className="w-full text-sm" style={{ minWidth: `${2350 + (previewResultStopColumnCount * 260)}px` }}>
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="px-3 py-2 text-center font-medium text-slate-600 w-20">
                                <input
                                  type="checkbox"
                                  checked={Object.keys(assignmentApproval).length > 0 && Object.values(assignmentApproval).every(Boolean)}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setAssignmentApproval((prev) => {
                                      const next = { ...prev };
                                      for (const key of Object.keys(next)) next[key] = checked;
                                      return next;
                                    });
                                  }}
                                  className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500 cursor-pointer"
                                />
                              </th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Plan</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Başlangıç</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Bitiş</th>
                              {Array.from({ length: previewResultStopColumnCount }, (_, index) => (
                                <th key={`stop-col-${index + 1}`} className="px-3 py-2 text-left font-medium text-slate-600">Boşaltma {index + 1}</th>
                              ))}
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Kanal</th>
                              <th className="px-3 py-2 text-center font-medium text-slate-600">Uğrama</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Karar</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Seçili Araç</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Rota</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">En Yakın Özmal</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">En Yakın Neden Atanmadı</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Seçilme Nedeni</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Özmal Adayları (Araç Araç)</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Sürücü</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Son Konum Kaynağı</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">İkamet İli</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Kalan Tako</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Hafta Kalan (Önce)</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Hafta Kalan (Sonra)</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">2 Hafta Kalan (Önce)</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">2 Hafta Kalan (Sonra)</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Yükleme Servis</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Son Konum → Yükleme (Yol Km)</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Boş Km</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Hub</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Hub Km</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Rota Km</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Gün</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Yatış</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Hesap</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Atama Başlangıç Tarihi</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Atama Başlangıç Saati</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Toplam Km</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Özmal Maliyet</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Spot Maliyet</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Kar/Gün</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Yüklemeye Varış</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Rotayı Bitiriş</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Huba Varış</th>
                            </tr>
                          </thead>
                          <tbody>
                            {decisionPreview.selectedScenario.decisions.map((item) => {
                              const rowKey = `${item.planCode}-${item.shipmentId}`;
                              const isSelected = selectedDecisionKey === rowKey;
                              const displayedOwn = getDisplayedOwnAlternative(item);
                              const parsedShipment = parsedShipments.find((shipment) => shipment.planCode === item.planCode);
                              const displayedStops = getDisplayedStopSchedules(item);
                              const vehicleTone = item.decision === 'OZMAL' ? getVehicleRowColor(item.assignedVehiclePlate) : null;
                              return (
                              <tr
                                key={rowKey}
                                tabIndex={0}
                                onClick={() => setSelectedDecisionKey(rowKey)}
                                onFocus={() => setSelectedDecisionKey(rowKey)}
                                className={`border-t border-slate-100 cursor-pointer outline-none ${isSelected ? 'ring-2 ring-violet-400 ring-inset' : 'hover:bg-slate-50'}`}
                              >
                                <td className="px-3 py-2 text-center" style={getVehicleCellStyle(vehicleTone, isSelected, true)}>
                                  <input
                                    type="checkbox"
                                    checked={assignmentApproval[item.planCode] ?? true}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      setAssignmentApproval((prev) => ({ ...prev, [item.planCode]: e.target.checked }));
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500 cursor-pointer"
                                  />
                                </td>
                                <td className="px-3 py-2 font-medium text-slate-900" style={getVehicleCellStyle(vehicleTone, isSelected)}>{item.planCode}</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{[item.pickupCity, item.pickupDistrict].filter(Boolean).join(' / ') || '-'}</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>
                                  <div className="min-w-[180px]">
                                    <div className="font-medium text-slate-900">{item.deliveryName || '-'}</div>
                                    {[item.deliveryCity, item.deliveryDistrict].filter(Boolean).length > 0 && (
                                      <div className="text-xs text-slate-500">{[item.deliveryCity, item.deliveryDistrict].filter(Boolean).join(' / ')}</div>
                                    )}
                                    {(() => {
                                      const allStops = getDisplayedDeliveryStops(item);
                                      if (allStops.length <= 1) return null;
                                      return allStops.map((stopLabel, idx) => (
                                        <div key={`stop-${idx}`} className="text-xs text-blue-600">{idx + 1}. {stopLabel}</div>
                                      ));
                                    })()}
                                  </div>
                                </td>
                                {Array.from({ length: previewResultStopColumnCount }, (_, stopIndex) => {
                                  const stop = displayedStops[stopIndex];
                                  return (
                                    <td key={`${rowKey}-stop-${stopIndex + 1}`} className="px-3 py-2 text-xs text-slate-700 align-top" style={getVehicleCellStyle(vehicleTone, isSelected)}>
                                      {stop ? (
                                        <div className="min-w-[220px] space-y-1">
                                          <div className="font-medium text-slate-900">{stop.label || `Boşaltma ${stopIndex + 1}`}</div>
                                          <div className="text-slate-500">Pencere: {stop.acceptanceWindowLabel || '-'}</div>
                                          <div className="text-slate-500">Tahmini varış: {formatDateTimeValue(stop.arrivalAt)}</div>
                                          <div>
                                            <span className={`inline-flex rounded-full px-2 py-1 font-semibold ${stop.windowMatched ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                              {stop.windowMatched ? 'Uygun' : 'Uygun Değil'}
                                            </span>
                                          </div>
                                          {!stop.windowMatched && getStopWindowMismatchReason(stop) ? (
                                            <div className="text-rose-700">{getStopWindowMismatchReason(stop)}</div>
                                          ) : null}
                                        </div>
                                      ) : '-'}
                                    </td>
                                  );
                                })}
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{item.deliveryChannelType || '-'}</td>
                                <td className="px-3 py-2 text-center" style={getVehicleCellStyle(vehicleTone, isSelected)}>
                                  <span className="inline-flex rounded-full bg-violet-100 text-violet-700 px-2 py-1 text-xs font-semibold">{item.stopCount ?? 1}</span>
                                </td>
                                <td className="px-3 py-2" style={getVehicleCellStyle(vehicleTone, isSelected)}>
                                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${item.decision === 'OZMAL' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                    {item.decision === 'OZMAL' ? 'Özmal' : 'Spot'}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>
                                  {item.decision === 'OZMAL' && item.assignedVehiclePlate ? (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openVehicleLocationInGoogleMaps(item.vehicleLatitude, item.vehicleLongitude);
                                      }}
                                      className="font-medium text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-800"
                                    >
                                      {item.assignedVehiclePlate}
                                    </button>
                                  ) : (item.assignedVehiclePlate || '-')}
                                </td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>
                                  {((item.decision === 'OZMAL' && item.vehicleLatitude != null && item.vehicleLongitude != null) || item.nearestOwnAlternative?.vehicleLatitude != null) ? (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openDecisionRouteInGoogleMaps(item);
                                      }}
                                      className="inline-flex items-center gap-1 rounded-lg border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700 hover:bg-violet-100"
                                    >
                                      <MapPinned className="h-3.5 w-3.5" />
                                      Rota Ac
                                    </button>
                                  ) : '-'}
                                </td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{item.nearestOwnAlternative?.assignedVehiclePlate || '-'}</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>
                                  {item.nearestOwnExclusionReason ? (
                                    <span className={`inline-flex rounded-full border px-2 py-1 text-[11px] leading-tight ${getDecisionReasonBadgeClass(item.nearestOwnExclusionReason)}`}>{item.nearestOwnExclusionReason}</span>
                                  ) : item.nearestOwnAlternative ? (
                                    <span className={`inline-flex rounded-full border px-2 py-1 text-[11px] leading-tight ${getDecisionReasonBadgeClass('Atandi', true)}`}>Atandi (en yakin ozmal)</span>
                                  ) : '-'}
                                </td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>
                                  {item.selectedOwnReason ? (
                                    <span className={`inline-flex rounded-full border px-2 py-1 text-[11px] leading-tight ${getDecisionReasonBadgeClass(item.selectedOwnReason, true)}`}>{item.selectedOwnReason}</span>
                                  ) : item.decision === 'SPOT' ? (
                                    <span className={`inline-flex rounded-full border px-2 py-1 text-[11px] leading-tight ${getDecisionReasonBadgeClass('Spot secildi')}`}>Spot secildi</span>
                                  ) : '-'}
                                </td>
                                <td className="px-3 py-2 align-top text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>
                                  {getOwnAlternativeCandidates(item).length > 0 ? (
                                    <div className="min-w-[420px] space-y-2 text-[11px] leading-5">
                                      {getOwnAlternativeCandidates(item).map((alt, altIndex) => (
                                        <div
                                          key={`${item.shipmentId}-alt-${alt.assignedVehicleId || altIndex}`}
                                          className={`rounded-lg border px-2 py-2 ${alt.selected ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}
                                        >
                                          <div className="flex items-start justify-between gap-2">
                                            <div>
                                              <div className="font-semibold text-slate-800">{alt.assignedVehiclePlate} {alt.driverName ? `- ${alt.driverName}` : ''}</div>
                                              <div className="text-slate-500">₺{Number(alt.selectedTotalCost || 0).toLocaleString('tr-TR')} | {Number(alt.driverToPickupKm || 0).toFixed(1)} km pickup | {Number(alt.totalKm || 0).toFixed(1)} km toplam</div>
                                            </div>
                                            <span className={`inline-flex rounded-full border px-2 py-1 text-[10px] font-semibold ${alt.selected ? 'border-emerald-200 bg-emerald-100 text-emerald-800' : 'border-slate-200 bg-white text-slate-600'}`}>
                                              {alt.selected ? 'Secildi' : 'Elendi'}
                                            </span>
                                          </div>
                                          <div className="mt-2 flex flex-wrap gap-1.5">
                                            <span className={`inline-flex rounded-full border px-2 py-1 text-[10px] leading-tight ${getDecisionReasonBadgeClass(alt.reason, alt.selected)}`}>{alt.reason}</span>
                                            <span className={`inline-flex rounded-full border px-2 py-1 text-[10px] leading-tight ${getDecisionCriteriaBadgeClass(alt.matchesPreferredDeliveryPoint)}`}>
                                              Teslim noktasi {alt.matchesPreferredDeliveryPoint == null ? 'bilinmiyor' : alt.matchesPreferredDeliveryPoint ? 'uyumlu' : 'uyumsuz'}
                                            </span>
                                            <span className={`inline-flex rounded-full border px-2 py-1 text-[10px] leading-tight ${getDecisionCriteriaBadgeClass(alt.matchesRegionRestriction)}`}>
                                              Bolge {alt.matchesRegionRestriction == null ? 'bilinmiyor' : alt.matchesRegionRestriction ? 'uyumlu' : 'uyumsuz'}
                                            </span>
                                            <span className={`inline-flex rounded-full border px-2 py-1 text-[10px] leading-tight ${getDecisionCriteriaBadgeClass(alt.weeklyDriveCompatible)}`}>
                                              Haftalik limit {alt.weeklyDriveCompatible == null ? 'bilinmiyor' : alt.weeklyDriveCompatible ? 'uygun' : 'uygun degil'}
                                            </span>
                                            <span className={`inline-flex rounded-full border px-2 py-1 text-[10px] leading-tight ${getDecisionCriteriaBadgeClass(alt.twoWeekDriveCompatible)}`}>
                                              2 haftalik limit {alt.twoWeekDriveCompatible == null ? 'bilinmiyor' : alt.twoWeekDriveCompatible ? 'uygun' : 'uygun degil'}
                                            </span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : '-'}
                                </td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{item.decision === 'OZMAL' ? (item.driverName || '-') : '-'}</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{item.decision === 'OZMAL' ? (item.locationSource || item.nearestOwnAlternative?.locationSource || '-') : (item.nearestOwnAlternative?.locationSource || '-')}</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{item.decision === 'OZMAL' ? (item.residenceCity || '-') : '-'}</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{item.decision === 'OZMAL' ? ((item.remainingTakoHours ?? item.nearestOwnAlternative?.remainingTakoHours) != null ? `${Number(item.remainingTakoHours ?? item.nearestOwnAlternative?.remainingTakoHours).toFixed(1)} saat` : '-') : '-'}</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{item.weeklyRemainingBefore != null ? `${Number(item.weeklyRemainingBefore).toFixed(1)} saat` : (getDisplayedWeeklyRemainingHours(item) != null ? `${Number(getDisplayedWeeklyRemainingHours(item)).toFixed(1)} saat` : '-')}</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{item.weeklyRemainingAfter != null ? `${Number(item.weeklyRemainingAfter).toFixed(1)} saat` : (item.decision === 'OZMAL' ? `${Number(item.weeklyRemainingBefore ?? getDisplayedWeeklyRemainingHours(item) ?? 0).toFixed(1)} saat` : '-')}</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{item.twoWeekRemainingBefore != null ? `${Number(item.twoWeekRemainingBefore).toFixed(1)} saat` : (getDisplayedTwoWeekRemainingHours(item) != null ? `${Number(getDisplayedTwoWeekRemainingHours(item)).toFixed(1)} saat` : '-')}</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{item.twoWeekRemainingAfter != null ? `${Number(item.twoWeekRemainingAfter).toFixed(1)} saat` : (item.decision === 'OZMAL' ? `${Number(item.twoWeekRemainingBefore ?? getDisplayedTwoWeekRemainingHours(item) ?? 0).toFixed(1)} saat` : '-')}</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{Number(displayedOwn?.pickupServiceDurationMinutes || item.spotAlternative?.pickupServiceDurationMinutes || 0) > 0 ? `${Number(displayedOwn?.pickupServiceDurationMinutes || item.spotAlternative?.pickupServiceDurationMinutes || 0)} dk` : '-'}</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{(() => {
                                  const km = item.decision === 'OZMAL'
                                    ? Number(item.driverToPickupKm || displayedOwn?.driverToPickupKm || 0)
                                    : Number(displayedOwn?.driverToPickupKm || 0);
                                  const method = item.decision === 'OZMAL'
                                    ? (item.driverToPickupDistanceMethod || displayedOwn?.driverToPickupDistanceMethod || null)
                                    : (displayedOwn?.driverToPickupDistanceMethod || null);
                                  if (km <= 0) return '-';
                                  const methodLabel = method === 'google_maps' ? 'Google' : method === 'openrouteservice' ? 'ORS' : method === 'osm' ? 'OSM' : method === 'bing' ? 'Bing' : method === 'haversine' ? 'Haversine' : null;
                                  return methodLabel ? `${km.toFixed(1)} km (${methodLabel})` : `${km.toFixed(1)} km`;
                                })()}</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{Number(displayedOwn?.emptyKm || item.emptyKm || 0).toFixed(1)} km</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{parsedShipment?.loadTypes?.join(', ') || '-'}</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{item.returnHubName || displayedOwn?.returnHubName || item.spotAlternative?.returnHubName || '-'}</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{Number(item.returnHubKm || displayedOwn?.returnHubKm || item.spotAlternative?.returnHubKm || 0).toFixed(1)} km</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{Number(displayedOwn?.routeKm || item.routeKm || 0).toFixed(1)} km</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{Number(displayedOwn?.operationDayCount || item.operationDayCount || 0).toFixed(0)}</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)} >₺{Math.round(Number(displayedOwn?.nightCost || item.nightCost || 0)).toLocaleString('tr-TR')}</td>
                                <td className="px-3 py-2" style={getVehicleCellStyle(vehicleTone, isSelected)}>
                                  <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                    (displayedOwn?.distanceMethod || item.distanceMethod) === 'google_maps' ? 'bg-blue-100 text-blue-700' :
                                    (displayedOwn?.distanceMethod || item.distanceMethod) === 'bing' ? 'bg-purple-100 text-purple-700' :
                                    (displayedOwn?.distanceMethod || item.distanceMethod) === 'openrouteservice' ? 'bg-cyan-100 text-cyan-700' :
                                    (displayedOwn?.distanceMethod || item.distanceMethod) === 'osm' ? 'bg-green-100 text-green-700' :
                                    (displayedOwn?.distanceMethod || item.distanceMethod) === 'bms' ? 'bg-indigo-100 text-indigo-700' :
                                    (displayedOwn?.distanceMethod || item.distanceMethod) === 'bms_failed' ? 'bg-slate-200 text-slate-500' :
                                    (displayedOwn?.distanceMethod || item.distanceMethod) === 'openrouteservice_failed' ? 'bg-slate-200 text-slate-500' :
                                    (displayedOwn?.distanceMethod || item.distanceMethod) === 'google_maps_failed' ? 'bg-slate-200 text-slate-500' :
                                    (displayedOwn?.distanceMethod || item.distanceMethod) === 'osm_failed' ? 'bg-slate-200 text-slate-500' :
                                    'bg-amber-100 text-amber-700'
                                  }`}>
                                    {{
                                      google_maps: 'Google Maps',
                                      bing: 'Bing Maps',
                                      openrouteservice: 'OpenRouteService',
                                      osm: 'OSM',
                                      bms: 'BMS',
                                      bms_failed: 'BMS Başarısız',
                                      openrouteservice_failed: 'OpenRouteService Başarısız',
                                      google_maps_failed: 'Google Başarısız',
                                      osm_failed: 'OSM Başarısız',
                                    }[(displayedOwn?.distanceMethod || item.distanceMethod)] || 'Haversine'}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-slate-700 text-xs" style={getVehicleCellStyle(vehicleTone, isSelected)}>{formatTurkeyDate(item.assignmentStartTime || item.estimatedPickupArrivalAt)}</td>
                                <td className="px-3 py-2 text-slate-700 text-xs" style={getVehicleCellStyle(vehicleTone, isSelected)}>{formatTurkeyTime(item.assignmentStartTime || item.estimatedPickupArrivalAt)}</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{Number(displayedOwn?.totalKm || item.totalKm || 0).toFixed(1)} km</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>
                                  <CostTooltip breakdown={
                                    <div className="space-y-1">
                                      <div className="font-semibold text-slate-900 mb-1">Özmal Maliyet Detayı</div>
                                      <div className="text-[11px] text-slate-500">Formül: km mazot maliyeti + personel + yatış x gün + köprü + bekleme + kısa mesafe primi</div>
                                      <div className="flex justify-between gap-4 text-slate-500"><span>Gün Sayısı</span><span>{Number(displayedOwn?.operationDayCount || 0).toFixed(0)} gün</span></div>
                                      <div className="flex justify-between gap-4 text-slate-500"><span>Toplam Operasyon Süresi</span><span>{Number(displayedOwn?.operationHours || 0).toFixed(2)} saat</span></div>
                                      <div className="flex justify-between gap-4 text-slate-500"><span>Sürüş / Mola / Dinlenme / Servis / Bekleme</span><span>{Number(displayedOwn?.totalDriveHours || 0).toFixed(2)} / {Number(displayedOwn?.totalBreakHours || 0).toFixed(2)} / {Number(displayedOwn?.totalRestHours || 0).toFixed(2)} / {Number(displayedOwn?.totalServiceHours || 0).toFixed(2)} / {Number(displayedOwn?.totalWaitingHours || 0).toFixed(2)} saat</span></div>
                                      <div className="flex justify-between gap-4 text-slate-500"><span>{Number(displayedOwn?.emptyKm || 0).toFixed(1)} boş + {Number(displayedOwn?.routeKm || 0).toFixed(1)} rota + {Number(displayedOwn?.returnHubKm || 0).toFixed(1)} hub</span><span>{Number(displayedOwn?.totalKm || 0).toFixed(1)} km</span></div>
                                      <div className="flex justify-between gap-4"><span>Yakıt Maliyeti = {Number(displayedOwn?.totalKm || 0).toFixed(1)} km × {Math.round(Number(displayedOwn?.kmCost || 0) / Math.max(Number(displayedOwn?.totalKm || 1), 1)).toLocaleString('tr-TR')} ₺/km (mazot)</span><span>₺{Math.round(Number(displayedOwn?.kmCost || 0)).toLocaleString('tr-TR')}</span></div>
                                      <div className="flex justify-between gap-4 text-sky-700"><span>{displayedOwn?.personnelCostMode === 'hourly' ? `Personel Maliyeti = ${Number(displayedOwn?.chargedPersonnelHours || 0).toFixed(2)} saat × ${Math.round(Number(displayedOwn?.hourlyPersonnelCost || 0)).toLocaleString('tr-TR')} ₺` : `Personel Maliyeti = ${Number(displayedOwn?.operationDayCount || 0).toFixed(0)} gün × ${Math.round(Number(displayedOwn?.dailyPersonnelCost || 0)).toLocaleString('tr-TR')} ₺`}</span><span>₺{Math.round(Number(displayedOwn?.personnelCost || 0)).toLocaleString('tr-TR')}</span></div>
                                      <div className="flex justify-between gap-4 text-slate-500"><span>Hafta / 2 Hafta Kalan</span><span>{displayedOwn?.remainingWeeklyDriveHours != null ? Number(displayedOwn.remainingWeeklyDriveHours).toFixed(1) : '-'} / {displayedOwn?.remainingTwoWeekDriveHours != null ? Number(displayedOwn.remainingTwoWeekDriveHours).toFixed(1) : '-'} saat</span></div>
                                      {Number(displayedOwn?.waitingCost || 0) > 0 && <div className="flex justify-between gap-4 text-amber-700"><span>Bekleme</span><span>₺{Math.round(Number(displayedOwn?.waitingCost || 0)).toLocaleString('tr-TR')}</span></div>}
                                      {Number(displayedOwn?.shortDistancePrime || 0) > 0 && <div className="flex justify-between gap-4 text-blue-700"><span>Kısa Mesafe Primi</span><span>₺{Math.round(Number(displayedOwn?.shortDistancePrime || 0)).toLocaleString('tr-TR')}</span></div>}
                                      <div className="flex justify-between gap-4 text-purple-700"><span>Yatış Bedeli = {Number(displayedOwn?.operationDayCount || 0).toFixed(0)} gün × {Math.round(Number(displayedOwn?.perNightCost || 1500)).toLocaleString('tr-TR')} ₺</span><span>₺{Math.round(Number(displayedOwn?.nightCost || (Number(displayedOwn?.operationDayCount || 0) * Number(displayedOwn?.perNightCost || 1500)))).toLocaleString('tr-TR')}</span></div>
                                      <div className="flex justify-between gap-4 text-red-700"><span>Köprü Maliyeti = Geçiş Maliyetleri Toplamı</span><span>₺{Math.round(Number(displayedOwn?.bridgeCost || 0)).toLocaleString('tr-TR')}</span></div>
                                      <div className="border-t border-slate-200 pt-1 mt-1 flex justify-between gap-4 font-semibold text-slate-900"><span>Toplam</span><span>₺{getOwnCostBreakdownTotal(displayedOwn).toLocaleString('tr-TR')}</span></div>
                                    </div>
                                  }>
                                    <span className="cursor-help border-b border-dotted border-slate-400">₺{getOwnCostBreakdownTotal(displayedOwn).toLocaleString('tr-TR')}</span>
                                  </CostTooltip>
                                </td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>
                                  <CostTooltip breakdown={
                                    <div className="space-y-1">
                                      <div className="font-semibold text-slate-900 mb-1">Spot Maliyet Detayı</div>
                                      {Number(item.spotAlternative?.directSpotCost || 0) > 0 ? (
                                        <div className="flex justify-between gap-4"><span>Baz Fiyat</span><span>₺{Math.round(Number(item.spotAlternative?.directSpotCost || 0)).toLocaleString('tr-TR')}</span></div>
                                      ) : (
                                        <div className="flex justify-between gap-4"><span>{Number(item.routeKm || item.nearestOwnAlternative?.routeKm || 0).toFixed(1)} km × {Math.round(Number(item.spotAlternative?.spotKmCost || 0) / Math.max(Number(item.routeKm || 1), 1)).toLocaleString('tr-TR')} ₺/km</span><span>₺{Math.round(Number(item.spotAlternative?.spotKmCost || 0)).toLocaleString('tr-TR')}</span></div>
                                      )}
                                      <div className="border-t border-slate-200 pt-1 mt-1 flex justify-between gap-4 font-semibold text-slate-900"><span>Toplam</span><span>₺{Math.round(Number(item.spotAlternative?.selectedTotalCost || item.spotAlternativeCost || 0)).toLocaleString('tr-TR')}</span></div>
                                    </div>
                                  }>
                                    <span className="cursor-help border-b border-dotted border-slate-400">₺{Math.round(Number(item.spotAlternative?.selectedTotalCost || item.spotAlternativeCost || 0)).toLocaleString('tr-TR')}</span>
                                  </CostTooltip>
                                </td>
                                <td className="px-3 py-2 font-semibold text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>
                                  ₺{Number(item.profitPerDay || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}
                                </td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{formatDateTimeValue(item.estimatedPickupArrivalAt)}</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{formatDateTimeValue(item.estimatedRouteFinishAt)}</td>
                                <td className="px-3 py-2 text-slate-700" style={getVehicleCellStyle(vehicleTone, isSelected)}>{formatDateTimeValue(item.estimatedNearestHubArrivalAt)}</td>
                              </tr>
                            )})}
                          </tbody>
                        </table>
                      </div>

                      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" style={SCROLL_WRAPPER_STYLE}>
                        <table className="w-full min-w-[700px] text-sm">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Senaryo</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Tür</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Özmal</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Spot</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Toplam Maliyet</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Sipariş Sırası</th>
                            </tr>
                          </thead>
                          <tbody>
                            {decisionPreview.scenarios.map((scenario) => (
                              <tr key={scenario.scenarioNo} className={`border-t border-slate-100 ${scenario.scenarioNo === decisionPreview.selectedScenario.scenarioNo ? 'bg-violet-50' : ''}`}>
                                <td className="px-3 py-2 font-medium text-slate-900">#{scenario.scenarioNo}</td>
                                <td className="px-3 py-2 text-slate-700">{scenario.scenarioType === 'deterministic' ? 'Baz' : 'Rastgele'}</td>
                                <td className="px-3 py-2 text-slate-700">{scenario.summary.ozmalAssigned}</td>
                                <td className="px-3 py-2 text-slate-700">{scenario.summary.spotAssigned}</td>
                                <td className="px-3 py-2 text-slate-700">₺{Math.round(scenario.summary.totalSelectedCost).toLocaleString('tr-TR')}</td>
                                <td className="px-3 py-2 text-xs text-slate-500">{Array.isArray(scenario.shipmentOrder) ? scenario.shipmentOrder.slice(0, 6).join(' → ') : '-'}{Array.isArray(scenario.shipmentOrder) && scenario.shipmentOrder.length > 6 ? ' ...' : ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-slate-500">{decisionPreviewLoading ? 'Karar destek hesaplanıyor...' : 'Henüz karar destek sonucu yok.'}</div>
                  )}
                </div>
              )}

              {previewTab === 'plans' && (
              <div className="overflow-x-auto rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-300" tabIndex={0} onKeyDown={handleHorizontalScrollKeyDown} style={SCROLL_WRAPPER_STYLE}>
                <table className="w-full min-w-[1700px] text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-center font-medium text-slate-600">KDS</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Plan Kodu</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Araç No</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Atama Başlangıç Tarihi</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Atama Başlangıç Saati</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Yükleme</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Boşaltma</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Ara Duraklar</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Müşteri Eşleşmesi</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Uğrama</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Hacim</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Yük Türü</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Araç Tipi</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Sürücü</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Notlar</th>
                    </tr>
                  </thead>
                      <tbody>
                      {parsedShipments.map((s, idx) => (
                        <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={!s.excludeFromKds}
                              onChange={(e) => setParsedShipments((current) => current.map((item, itemIndex) => itemIndex === idx ? { ...item, excludeFromKds: !e.target.checked } : item))}
                              className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500 cursor-pointer"
                              title="Karar destek modeline kat"
                            />
                          </td>
                          <td className="px-3 py-2 font-medium text-blue-700">{s.planCode || '-'}</td>
                          <td className="px-3 py-2 font-semibold text-slate-900">{s.arac_no}</td>
                        <td className="px-3 py-2">
                          <input
                            type="date"
                            value={s.assignmentStartDate || ''}
                            onChange={(e) => {
                              setAlignAssignmentStartBackwardFromDeliveryWindow(false);
                              setParsedShipments((current) => current.map((item, itemIndex) => itemIndex === idx ? { ...item, assignmentStartDate: e.target.value, pickupDate: combineAssignmentStart(e.target.value, item.assignmentStartTime) } : item));
                            }}
                            className="w-full rounded-lg border border-slate-300 px-2 py-1 text-xs"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="time"
                            value={s.assignmentStartTime || ''}
                            onChange={(e) => {
                              setAlignAssignmentStartBackwardFromDeliveryWindow(false);
                              setParsedShipments((current) => current.map((item, itemIndex) => itemIndex === idx ? { ...item, assignmentStartTime: e.target.value, pickupDate: combineAssignmentStart(item.assignmentStartDate, e.target.value) } : item));
                            }}
                            className="w-full rounded-lg border border-slate-300 px-2 py-1 text-xs"
                          />
                          {alignAssignmentStartBackwardFromDeliveryWindow && getBackwardPlannedAssignmentExplanation(s) ? (
                            <div className="mt-1 max-w-[320px] text-[11px] leading-4 text-violet-700">
                              {getBackwardPlannedAssignmentExplanation(s)}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-slate-700">{s.pickupWarehouseName}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-900">{s.deliveryCustomerName}</div>
                          <div className="text-xs text-slate-500">{s.deliveryCustomerCode}</div>
                          <div className="text-xs text-slate-400">{s.deliveryCustomerCity} / {s.deliveryCustomerDistrict}</div>
                        </td>
                        <td className="px-3 py-2">
                          {s.stops.length > 1 ? (
                            <div className="space-y-1">
                              {s.stops.slice(1).map((stop, si) => (
                                <div key={si} className="text-xs text-slate-600">
                                  {si + 2}. {stop.customerName} ({stop.city}) - {stop.volume.toFixed(2)} m³
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="space-y-1">
                            {s.stops.map((stop, si) => (
                              <div key={`${stop.customerCode}-${si}`} className="flex items-center gap-2 text-xs">
                                {stop.customerType === 'warehouse' ? (
                                  <>
                                    <CheckCircle2 className="h-3.5 w-3.5 text-blue-600" />
                                    <span className="text-blue-700">{si + 1}. durak depo baglandi</span>
                                    <span className="text-slate-500">Kod: {stop.customerCode}</span>
                                  </>
                                ) : stop.existingCustomerId ? (
                                  <>
                                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                                    <span className="text-emerald-700">{si + 1}. durak baglandi</span>
                                    <span className="text-slate-500">ID: {stop.existingCustomerId}</span>
                                  </>
                                ) : (
                                  <>
                                    <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
                                    <span className="text-amber-700">{si + 1}. durak yeni musteri olusturacak</span>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className="inline-flex rounded-full bg-violet-100 text-violet-700 px-2 py-1 text-xs font-semibold">
                            {s.stopCount}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-700">{s.totalVolume.toFixed(2)} m³</td>
                        <td className="px-3 py-2 text-slate-700">{s.loadTypes.join(', ') || '-'}</td>
                        <td className="px-3 py-2 text-slate-700">{s.vehicleType}</td>
                        <td className="px-3 py-2 text-slate-700">{decisionPreview?.selectedScenario?.decisions?.find((item) => item.planCode === s.planCode)?.driverName || '-'}</td>
                        <td className="px-3 py-2 text-xs text-slate-500 max-w-[200px] truncate" title={s.notes.join(' | ')}>
                          {s.notes.join(' | ') || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}

              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">{parsedShipments.length}</span> sevk siparişi oluşturulacak
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => setStep('warehouse')} className="rounded-xl border border-slate-200 px-5 py-3 text-sm font-medium text-slate-600 hover:bg-slate-50">Geri</button>
                  <button onClick={handleCreate} disabled={creating} className="rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60 inline-flex items-center gap-2">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    {creating ? 'Oluşturuluyor...' : 'Onayla ve Oluştur'}
                  </button>
                </div>
              </div>

              {createErrors.length > 0 && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                  <div className="font-semibold text-red-800 mb-2">Hatalar:</div>
                  <ul className="text-sm text-red-700 space-y-1">
                    {createErrors.map((err, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                        {err}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {showDecisionTimeline && decisionTimelineWindow && decisionTimelineScale ? (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/55 p-2 sm:p-3">
            <div className="h-[96vh] w-full max-w-[98vw] overflow-hidden rounded-3xl bg-white shadow-2xl xl:max-w-[1900px]" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
                <div>
                  <div className="text-lg font-semibold text-slate-900">Karar Destek Zaman Cizelgesi</div>
                  <div className="text-xs text-slate-500">
                    Baslangic: {formatTimelineDateTime(decisionTimelineWindow.start.toISOString())} | Bitis: {formatTimelineDateTime(decisionTimelineWindow.end.toISOString())}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setDecisionTimelineZoom((current) => Math.max(0.2, Number((current - 0.2).toFixed(2))))} className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50">-</button>
                  <div className="min-w-[64px] text-center text-xs font-semibold text-slate-600">%{Math.round(decisionTimelineZoom * 100)}</div>
                  <button type="button" onClick={() => setDecisionTimelineZoom((current) => Math.min(2.4, Number((current + 0.2).toFixed(2))))} className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50">+</button>
                  <button type="button" onClick={() => setShowDecisionTimeline(false)} className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-50">
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div ref={decisionTimelineScrollRef} className="h-[calc(96vh-76px)] overflow-auto bg-slate-50 p-3 sm:p-4 cursor-grab active:cursor-grabbing" onMouseDown={handleDecisionTimelineDragStart} onMouseMove={handleDecisionTimelineDragMove} onMouseUp={handleDecisionTimelineDragEnd} onMouseLeave={handleDecisionTimelineDragEnd}>
                <div className="min-w-[1500px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="sticky top-0 z-10 grid border-b border-slate-200 bg-white" style={{ gridTemplateColumns: `320px ${decisionTimelineScale.width}px` }}>
                    <div className="border-r border-slate-200 px-4 py-4">
                      <div className="text-sm font-semibold text-slate-900">Siparişler</div>
                      <div className="mt-1 text-xs text-slate-500">Her satır bir sipariş rotasını gösterir</div>
                    </div>
                    <div className="relative h-24 bg-slate-50">
                      {decisionTimelineScale.dayBands.map((band) => (
                        <div key={band.key} className="absolute top-0 h-9 border-r border-slate-200 bg-slate-100/80 px-2 py-2 text-xs font-semibold text-slate-700" style={{ left: band.left, width: band.width }}>
                          {band.label}
                        </div>
                      ))}
                      {decisionTimelineScale.ticks.map((tick, index) => {
                        const left = ((tick.getTime() - decisionTimelineScale.start.getTime()) / (60 * 60 * 1000)) * decisionTimelineScale.hourWidth;
                        const isHourTick = tick.getMinutes() === 0;
                        const showLabel = isHourTick && (decisionTimelineZoom >= 1.4 || tick.getHours() % (decisionTimelineZoom >= 1 ? 2 : 4) === 0);
                        return (
                          <div key={tick.toISOString()} className="absolute bottom-0 top-0" style={{ left, width: decisionTimelineScale.hourWidth / 2 }}>
                            <div className={`absolute inset-y-0 left-0 w-px ${isHourTick ? 'bg-slate-300' : 'bg-slate-200/70'}`} />
                            {showLabel ? <div className="px-1 pt-11 text-[11px] font-semibold text-slate-700">{tick.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</div> : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {decisionTimelineRows.map((row: any) => {
                    const totalWindowMs = Math.max(1, decisionTimelineScale.end.getTime() - decisionTimelineScale.start.getTime());
                    const milestones = [
                      { key: 'assignment', label: 'Atama', at: row.assignmentStart, color: 'bg-slate-700' },
                      { key: 'pickup', label: 'Yükleme', at: row.pickupArrival, color: 'bg-blue-500' },
                      ...row.stopEvents.flatMap((stop: any, index: number) => ([
                        stop.arrivalAt ? { key: `stop-arrival-${index}`, label: `${stop.label} varış`, at: stop.arrivalAt, color: 'bg-amber-500' } : null,
                        stop.serviceEndAt ? { key: `stop-end-${index}`, label: `${stop.label} çıkış`, at: stop.serviceEndAt, color: 'bg-emerald-500' } : null,
                      ].filter(Boolean))),
                      { key: 'finish', label: 'Rota bitiş', at: row.routeFinish, color: 'bg-rose-500' },
                      { key: 'hub', label: 'Hub', at: row.hubArrival, color: 'bg-violet-500' },
                    ].filter((item: any) => item?.at);

                    return (
                      <div key={row.key} className="grid border-b border-slate-100 last:border-b-0" style={{ gridTemplateColumns: `320px ${decisionTimelineScale.width}px` }}>
                        <div className="border-r border-slate-100 px-4 py-3">
                          <div className="text-sm font-semibold text-slate-900">{row.planCode} • {row.decisionLabel}</div>
                          <div className="mt-1 text-xs font-medium text-slate-600">{row.vehicleLabel}</div>
                          <div className="mt-2 text-xs text-slate-500">{row.pickupLabel}</div>
                          <div className="text-xs text-slate-400">{'->'} {row.deliveryLabel}</div>
                          <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                            <span className="rounded-full bg-blue-50 px-2 py-1 font-medium text-blue-700">Sürüş {Math.round((row.declaredDriveMinutes || 0) / 6) / 10} sa</span>
                            <span className="rounded-full bg-amber-50 px-2 py-1 font-medium text-amber-700">Bekleme {Math.round((row.declaredWaitingMinutes || 0) / 6) / 10} sa</span>
                            <span className="rounded-full bg-emerald-50 px-2 py-1 font-medium text-emerald-700">Servis {Math.round((row.declaredServiceMinutes || 0) / 6) / 10} sa</span>
                          </div>
                          {row.warnings?.length ? <div className="mt-2 text-[10px] leading-4 text-amber-700">{row.warnings.join(' | ')}</div> : null}
                        </div>

                        <div className="relative h-28 bg-white">
                          {decisionTimelineScale.ticks.map((tick, index) => (
                            <div key={`${row.key}-${tick.toISOString()}`} className="absolute bottom-0 top-0 w-px bg-slate-100" style={{ left: index * decisionTimelineScale.hourWidth }} />
                          ))}
                          <div className="absolute inset-x-0 top-6 h-px bg-slate-100" />
                          <div className="absolute inset-x-0 top-14 h-px bg-slate-100" />
                          <div className="absolute inset-x-0 top-20 h-px bg-slate-100" />

                          {row.segments.map((segment: any) => {
                            const segmentStart = parseValidDate(segment.start);
                            const segmentEnd = parseValidDate(segment.end);
                            if (!segmentStart || !segmentEnd) return null;
                            const left = ((segmentStart.getTime() - decisionTimelineScale.start.getTime()) / totalWindowMs) * decisionTimelineScale.width;
                            const width = Math.max(6, ((segmentEnd.getTime() - segmentStart.getTime()) / totalWindowMs) * decisionTimelineScale.width);
                            const colorClass = segment.kind === 'drive' ? 'bg-blue-500' : segment.kind === 'wait' ? 'bg-amber-400' : segment.kind === 'rest' ? 'bg-violet-500' : 'bg-emerald-500';
                            return <div key={segment.key} title={`${segment.label}: ${formatTimelineDateTime(segment.start)} - ${formatTimelineDateTime(segment.end)}`} className={`absolute top-10 h-8 rounded-md ${colorClass} opacity-90 shadow-sm`} style={{ left, width }} />;
                          })}

                          {milestones.map((milestone: any) => {
                            const at = parseValidDate(milestone.at);
                            if (!at) return null;
                            const left = ((at.getTime() - decisionTimelineScale.start.getTime()) / totalWindowMs) * decisionTimelineScale.width;
                            const milestoneIndex = milestones.findIndex((item: any) => item.key === milestone.key);
                            const compactLabel = milestone.label.length > 14 ? milestone.label.slice(0, 12) + '…' : milestone.label;
                            return (
                              <div key={milestone.key} className="absolute -translate-x-1/2" style={{ left, top: milestoneIndex % 2 === 0 ? 2 : 18 }} title={`${milestone.label}: ${formatTimelineDateTime(milestone.at)}`}>
                                <div className={`mx-auto h-3.5 w-3.5 rounded-full border-2 border-white shadow ${milestone.color}`} />
                                {(decisionTimelineZoom >= 1.1 || milestoneIndex < 3 || milestone.key === 'finish' || milestone.key === 'hub') ? <div className="mt-1 whitespace-nowrap text-center text-[10px] font-medium text-slate-600">{compactLabel}</div> : null}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
